import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  FaReceipt,
  FaFileInvoiceDollar,
  FaPlus,
  FaEye,
  FaEdit,
  FaDownload,
  FaShareSquare,
  FaEnvelope,
  FaTrash,
  FaSearch,
  FaClock,
  FaCheck,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaFilter,
  FaCheckCircle,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaThLarge,
  FaList,
  FaColumns,
  FaCheckSquare,
  FaSquare,  FaTimes,
  FaWhatsapp,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaCalendarAlt,
  FaExchangeAlt
} from 'react-icons/fa';
import Modal from '../components/Modal/Modal';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import PageHero from '../components/PageHero/PageHero';
import { useLocation } from 'react-router-dom';
import './Billing.scss';
import { storage } from '../utils/storage';
import api from '../utils/api';
import { STORAGE_KEYS, APP_NAME, API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import { useLanguage } from '../context/LanguageContext';
import { formatDateDisplay, formatDateISO } from '../utils/date';
import { addNotification } from '../redux/slices/uiSlice';
import { saveColumnPreferences, updateColumnPreferences } from '../redux/slices/settingsSlice';
import { showToast } from '../utils/toast';
import useConfirm from '../hooks/useConfirm';
import Highlight from '../utils/Highlight';
import ServiceTypeInput from '../components/ServiceTypeInput/ServiceTypeInput';
import ServiceTags from '../components/ServiceTags/ServiceTags';
import ShareChoiceModal from '../components/ShareChoiceModal/ShareChoiceModal';
import { buildInvoiceWhatsApp, buildInvoiceEmail } from '../utils/shareTemplates';

// Normalize orderType: events use 'suborder', quotations use 'sub' — billing expects 'sub'
const normalizeOrderType = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'sub' || v === 'suborder') return 'sub';
  return 'own';
};

// Draft key for billing create form (session-only)
const BILLING_DRAFT_KEY = 'photoflow_billing_draft';
const BILLING_COLUMN_DEFAULTS = {
  id: true,
  quotationId: false,
  eventId: false,
  orderType: false,
  services: true,
  amount: true,
  paid: true,
  balance: true,
  status: true,
};

const Billing = () => {
  const { t } = useLanguage();
  // Get company and bank details from Redux settings
  const { company, bank, notifications, columnPreferences } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  
  // Initialize with empty array - data will be loaded from server only
  const [invoices, setInvoices] = useState([]);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrderType, setFilterOrderType] = useState('all');
  const [statFilter, setStatFilter] = useState(null);
  // Invoice date range filter (YYYY-MM-DD)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'name', 'amount', 'date'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  // Default to grid view on mobile devices (width < 768px)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'grid';
    }
    return 'table';
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(BILLING_COLUMN_DEFAULTS);
  // Share dialog: which invoice + which channel (whatsapp | email)
  const [shareTarget, setShareTarget] = useState(null);
  const [shareChannel, setShareChannel] = useState('whatsapp');
  // Refs for column menu and toggle button to detect outside clicks
  const billingMenuRef = useRef(null);
  const confirm = useConfirm();
  const billingBtnRef = useRef(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paymentMethod: 'Bank Transfer',
    paymentDate: formatDateISO(new Date()),
    reference: '',
    notes: ''
  });

  useEffect(() => {
    const savedColumns = columnPreferences?.billing;
    if (savedColumns && typeof savedColumns === 'object' && !Array.isArray(savedColumns)) {
      setVisibleColumns({
        ...BILLING_COLUMN_DEFAULTS,
        ...savedColumns,
      });
    }
  }, [columnPreferences?.billing]);

  // available services loaded from Services page
  const [availableServices, setAvailableServices] = useState([]);
  // Load policies/terms for Terms & Conditions (grouped by policy.group)
  const [groupedPolicies, setGroupedPolicies] = useState({});
  
  // Track pending new services that haven't been saved to database yet
  const [pendingNewServices, setPendingNewServices] = useState([]);

  // Combine available services and pending new services for dropdown display
  const allServices = useMemo(() => {
    return [...availableServices, ...pendingNewServices];
  }, [availableServices, pendingNewServices]);

  // Track a new service locally (will be saved to database only when form is submitted)
  const handleNewService = async (serviceName) => {
    if (!serviceName || !serviceName.trim()) return;
    
    const trimmedName = serviceName.trim();
    
    // Check if service already exists in either available or pending lists (case-insensitive)
    const existsInAvailable = availableServices.some(s => 
      (s.name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    const existsInPending = pendingNewServices.some(s => 
      (s.name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (existsInAvailable || existsInPending) return;

    const newService = {
      id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: trimmedName,
      category: 'Uncategorized',
      description: '',
      basePrice: 0,
      deliverables: [],
      features: [],
    };

    // Add to pending services (not saved to database yet)
    setPendingNewServices(prev => [...prev, { id: newService.id, name: newService.name, price: 0 }]);
  };

  // Save a single pending service to the database
  const savePendingServiceToDatabase = async (service) => {
    try {
      const token = session.getToken();
      if (!token) return null;

      const res = await fetch(`${API_BASE_URL}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(service),
      });

      if (res.ok) {
        const created = await res.json();
        return { id: created._id || created.id, name: created.name, price: Number(created.basePrice) || 0 };
      }
    } catch (e) {
      console.error('Failed to save new service:', e);
    }
    return null;
  };
  
  // available quotations loaded from server (database)
  const [quotations, setQuotations] = useState([]);
  // Dropdown-specific cached list & error for immediate UI feedback
  const [dropdownQuotations, setDropdownQuotations] = useState([]);
  const [dropdownQuotationsError, setDropdownQuotationsError] = useState('');
  // Local UI state for searchable quotation dropdown
  const [quotationQuery, setQuotationQuery] = useState('');
  const [showQuotationDropdown, setShowQuotationDropdown] = useState(false);
  const [highlightedQuotationIndex, setHighlightedQuotationIndex] = useState(-1);
  const quotationBlurTimeoutRef = useRef(null);
  const quotationInputRef = useRef(null);
  
  // available events loaded from server (database)
  const [events, setEvents] = useState([]);
  // Dropdown-specific cached list & error for immediate UI feedback
  const [dropdownEvents, setDropdownEvents] = useState([]);
  const [dropdownEventsError, setDropdownEventsError] = useState('');
  // Local UI state for searchable event dropdown
  const [eventQuery, setEventQuery] = useState('');
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [highlightedEventIndex, setHighlightedEventIndex] = useState(-1);
  const eventBlurTimeoutRef = useRef(null);
  const eventInputRef = useRef(null);
  
  // Close column menu when clicking outside (but ignore clicks on the toggle button)
  useEffect(() => {
    function handleOutsideClick(e) {
      if (!showColumnMenu) return;
      const target = e.target;
      if (billingMenuRef.current && billingMenuRef.current.contains(target)) return;
      if (billingBtnRef.current && billingBtnRef.current.contains(target)) return;
      setShowColumnMenu(false);
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showColumnMenu]);
  
  useEffect(() => {
    try {
      const persisted = storage.getJSON(STORAGE_KEYS.SERVICES, null);
      if (Array.isArray(persisted) && persisted.length > 0) {
        const mapped = persisted.map(s => ({ id: s.id, name: s.name, price: Number(s.basePrice) || 0 }));
        setAvailableServices(mapped);
      }
    } catch (e) {
      // ignore and use fallback
    }
    // Also try to fetch fresh services from server (preferred when authenticated)
    (async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.SERVICES);
        if (Array.isArray(remote) && remote.length > 0) {
          const mapped = remote.map(s => ({ id: s.id, name: s.name, price: Number(s.basePrice) || Number(s.price) || 0 }));
          setAvailableServices(mapped);
        }
      } catch (e) {
        // ignore - keep persisted or empty
      }
    })();
    // load quotations from server
    (async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.QUOTATIONS);
        if (Array.isArray(remote) && remote.length > 0) {
          setQuotations(remote);
        }
      } catch (e) {
        // ignore
      }

      // Ensure dropdown helper is populated
      try { loadDropdownQuotations(); } catch (e) {}
    })();
  }, []);

  // Load policies/terms (grouped) for Terms & Conditions selection
  useEffect(() => {
    let mounted = true;
    (async () => {
      // Policies: local first
      try {
        const policies = storage.getJSON(STORAGE_KEYS.POLICY, []);
        if (Array.isArray(policies) && policies.length > 0) {
          const grouped = {};
          policies.forEach(policy => {
            const group = policy.group || 'Uncategorized';
            if (!grouped[group]) grouped[group] = [];
            grouped[group].push(policy);
          });
          setGroupedPolicies(grouped);
        }
      } catch (e) {
        // ignore
      }

      // Try to fetch policies from server
      try {
        const remotePolicies = await storage.syncFromServer(STORAGE_KEYS.POLICY);
        if (mounted && Array.isArray(remotePolicies) && remotePolicies.length > 0) {
          const grouped = {};
          remotePolicies.forEach(policy => {
            const group = policy.group || 'Uncategorized';
            if (!grouped[group]) grouped[group] = [];
            grouped[group].push(policy);
          });
          setGroupedPolicies(grouped);
        }
      } catch (e) {
        // ignore
      }
    })();

    return () => { mounted = false; };
  }, []);

  // Listen for notifications dispatched while user is already on this page
  // (some navigation events send state only when moving between pages). This
  // allows the notification dropdown to open a view even if we're already
  // on /billing.
  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/billing') return;
        const state = detail.state || {};
        if (state.openCreate) {
          setShowCreateModal(true);
        }
        if (state.openViewId) {
          const idToOpen = String(state.openViewId);
          const inv = invoices.find(i => [i.id, i._id].map(v => v ? String(v) : '').includes(idToOpen));
          if (inv) {
            handleViewInvoice(inv);
          } else {
            pendingOpenRef.current = idToOpen;
          }
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [invoices]);

  useEffect(() => {
    const handleEventsChanged = () => {
      refreshEventsFromServer();
      loadDropdownEvents();
    };

    window.addEventListener('photoflow:events-changed', handleEventsChanged);
    return () => window.removeEventListener('photoflow:events-changed', handleEventsChanged);
  }, []);

  // Try to load invoices from server when authenticated. Falls back to local storage.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const remote = await storage.syncFromServer(STORAGE_KEYS.INVOICES);
        if (mounted && Array.isArray(remote) && remote.length > 0) {
          setInvoices(remote);
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  const location = useLocation();
  const pendingOpenRef = useRef(null);

  useEffect(() => {
    if (location?.state?.openCreate) {
      // Open create modal (no draft loading from storage)
      setShowCreateModal(true);
      try { window.history.replaceState({}, document.title); } catch (e) {}
    }

    // Deep-link for push clicks when app was closed (SW opens /billing?id=...)
    // Supports both location.state.openViewId (in-app) and ?id= query param (closed-app)
    const getDeepLinkId = () => {
      if (location?.state?.openViewId) return String(location.state.openViewId);
      try {
        const params = new URLSearchParams(window.location.search);
        const qId = params.get('id');
        if (qId) return String(qId);
      } catch (e) {}
      return null;
    };
    const idToOpen = getDeepLinkId();
    if (idToOpen) {
      const inv = invoices.find(i => [i.id, i._id].map(v => v ? String(v) : '').includes(idToOpen));
      if (inv) {
        handleViewInvoice(inv);
      } else {
        pendingOpenRef.current = idToOpen;
      }
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('id')) {
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          window.history.replaceState({}, document.title);
        }
      } catch (e) {}
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle push deep-link when app is already open (SW navigates existing tab)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId && !pendingOpenRef.current) {
        const inv = invoices.find(i => [i.id, i._id].map(v => v ? String(v) : '').includes(String(qId)));
        if (inv) {
          handleViewInvoice(inv);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          pendingOpenRef.current = String(qId);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // If a pending open id was set (because invoices weren't loaded when navigation happened),
  // try to open it now that `invoices` may have been populated.
  useEffect(() => {
    if (pendingOpenRef.current) {
      const idToOpen = String(pendingOpenRef.current);
      const inv = invoices.find(i => [i.id, i._id].map(v => v ? String(v) : '').includes(idToOpen));
      if (inv) {
        handleViewInvoice(inv);
        pendingOpenRef.current = null;
        try { window.history.replaceState({}, document.title); } catch (e) {}
      }
    }
  }, [invoices]);

  const handleRecordPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: invoice.amount - invoice.paid,
      paymentMethod: 'Bank Transfer',
      paymentDate: formatDateISO(new Date()),
      reference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const generateInvoiceId = () => {
    const year = new Date().getFullYear();
    const seq = String(Date.now()).slice(-6);
    return `INV-${year}-${seq}`;
  };

  // Preview ID shown in the create form so user can see the invoice id before creating
  const [previewInvoiceId, setPreviewInvoiceId] = useState('');

  const [newInvoiceData, setNewInvoiceData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    orderType: 'own',
    items: [{ service: '', description: '', quantity: 1, price: 0 }],
    additionalCharges: [],
    issueDate: formatDateISO(new Date()),
    quotationId: '',
    eventId: '',
    terms: [],
    showTermsInPdf: true,
    showDescriptionInPdf: false,
    showPaymentDetailsInPdf: true,
    showQrCodeInPdf: true
  });

  // Checkbox state for quotation ID form
  const [useQuotationData, setUseQuotationData] = useState(true);
  
  // Toggle mode between quotation and event
  const [referenceMode, setReferenceMode] = useState('quotation'); // 'quotation' or 'event'

  // Draft helpers for billing create form (component state only - no persistence)
  const makeBillingDraftKey = (id) => (id ? `${BILLING_DRAFT_KEY}_${String(id)}` : BILLING_DRAFT_KEY);

  const saveBillingDraftToSession = (key) => {
    const data = { newInvoiceData, previewInvoiceId };
    sessionStorage.setItem(key, JSON.stringify(data));
  };

  const loadBillingDraftFromSession = (key) => {
    try {
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  };

  const removeBillingDraftFromSession = (key) => {
    sessionStorage.removeItem(key);
  };

  // Helper to get an empty invoice data object (used to reset form)
  const getEmptyInvoiceData = () => ({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    orderType: 'own',
    items: [{ service: '', description: '', quantity: 1, price: 0 }],
    additionalCharges: [],
    issueDate: formatDateISO(new Date()),
    quotationId: '',
    eventId: '',
    terms: [],
    showTermsInPdf: true,
    showDescriptionInPdf: false,
    showPaymentDetailsInPdf: true,
    showQrCodeInPdf: true
  });

  // Auto-save draft when form data changes and modal is open
  useEffect(() => {
    if (showCreateModal && newInvoiceData) {
      // Check if form has any data (not empty)
      const hasData = newInvoiceData.clientName || newInvoiceData.clientEmail || newInvoiceData.clientPhone ||
                      newInvoiceData.items.some(item => item.service || item.description || item.price > 0) ||
                      newInvoiceData.additionalCharges.length > 0 || newInvoiceData.quotationId || newInvoiceData.eventId;
      if (hasData) {
        saveBillingDraftToSession(BILLING_DRAFT_KEY);
      }
    }
  }, [newInvoiceData, showCreateModal]);

  // When a quotation is chosen from the select, prefill invoice fields
  const handleSelectQuotation = (quotationId) => {
    if (!quotationId) {
      setNewInvoiceData({ ...newInvoiceData, quotationId: '', eventId: '' });
      return;
    }

    // Prefer in-memory state, but fall back to dropdown helper so selection works even if state wasn't populated
    const list = Array.isArray(quotations) && quotations.length > 0 ? quotations : getDropdownQuotations();
    const q = Array.isArray(list) ? list.find(x => String(x.id || x.quotationId || x.ref) === String(quotationId)) : null;
    if (!q) {
      setNewInvoiceData({ ...newInvoiceData, quotationId, eventId: '' });
      return;
    }

    // Normalize the id field
    const qId = String(q.id || q.quotationId || q.ref);

    // Build items: prefer saved items, otherwise map services
    const items = (Array.isArray(q.items) && q.items.length > 0)
      ? q.items.map(it => ({ service: it.service || '', description: it.description || '', quantity: it.quantity || 1, price: it.price || 0 }))
      : (Array.isArray(q.services) ? q.services.map(svc => ({ service: svc, description: '', quantity: 1, price: 0 })) : []);

    setNewInvoiceData({
      ...newInvoiceData,
      quotationId: qId,
      eventId: '', // Clear event ID when quotation is selected
      orderType: normalizeOrderType(q.orderType) || newInvoiceData.orderType,
      clientName: q.clientName || newInvoiceData.clientName,
      clientEmail: q.clientEmail || newInvoiceData.clientEmail,
      clientPhone: q.clientPhone || newInvoiceData.clientPhone,
      items: items.length > 0 ? items : newInvoiceData.items,
      additionalCharges: Array.isArray(q.additionalCharges) ? q.additionalCharges : newInvoiceData.additionalCharges,
      terms: Array.isArray(q.terms) ? q.terms : (Array.isArray(q.terms) ? q.terms : newInvoiceData.terms),
      showTermsInPdf: q.showTermsInPdf === false ? false : true
    });
  };

  // When an event is chosen from the select, prefill invoice fields
  const handleSelectEvent = (eventId) => {
    if (!eventId) {
      setNewInvoiceData({ ...newInvoiceData, eventId: '', quotationId: '' });
      return;
    }

    // Prefer in-memory state, but fall back to dropdown helper
    const list = Array.isArray(events) && events.length > 0 ? events : getDropdownEvents();
    const e = Array.isArray(list) ? list.find(x => String(x.id || x.quotationId) === String(eventId)) : null;
    if (!e) {
      setNewInvoiceData({ ...newInvoiceData, eventId, quotationId: '' });
      return;
    }

    // Normalize the id field
    const eId = String(e.id || e.quotationId);

    // Build items from event services
    const items = Array.isArray(e.services)
      ? e.services.map(svc => ({
          service: typeof svc === 'string' ? svc : (svc.name || ''),
          description: '',
          quantity: 1,
          price: 0
        }))
      : [];

    setNewInvoiceData({
      ...newInvoiceData,
      eventId: eId,
      quotationId: '', // Clear quotation ID when event is selected
      orderType: normalizeOrderType(e.orderType) || newInvoiceData.orderType,
      clientName: e.clientName || newInvoiceData.clientName,
      clientEmail: e.clientEmail || newInvoiceData.clientEmail,
      clientPhone: e.clientPhone || newInvoiceData.clientPhone,
      items: items.length > 0 ? items : newInvoiceData.items,
      additionalCharges: []
    });
  };

  const handleOpenCreate = () => {
    // refresh quotations before opening so the dropdown shows latest values
    try {
      const persistedQ = storage.getJSON(STORAGE_KEYS.QUOTATIONS, null) || storage.getJSON(STORAGE_KEYS.APP_STATE, null) || null;
      if (Array.isArray(persistedQ)) {
        setQuotations(persistedQ);
      } else if (persistedQ && Array.isArray(persistedQ.quotations)) {
        setQuotations(persistedQ.quotations);
      }
    } catch (e) {}
    // Ensure synchronous dropdown read and errors are available for UI
    try { loadDropdownQuotations(); } catch (e) { /* ignore */ }

    // refresh events before opening so the dropdown shows latest values
    try {
      void refreshEventsFromServer();
    } catch (e) {}
    // Ensure synchronous dropdown read and errors are available for UI
    try { loadDropdownEvents(); } catch (e) { /* ignore */ }

    // If a draft exists for billing create, load it instead of blank defaults
    try {
      const draft = loadBillingDraftFromSession(makeBillingDraftKey());
      if (draft && typeof draft === 'object') {
        if (draft.newInvoiceData) setNewInvoiceData(draft.newInvoiceData);
        if (draft.previewInvoiceId) setPreviewInvoiceId(draft.previewInvoiceId);
        setShowCreateModal(true);
        return;
      }
    } catch (e) {}

    setNewInvoiceData(getEmptyInvoiceData());
    // generate a preview id for display
    setPreviewInvoiceId(generateInvoiceId());
    setEditingInvoiceId(null);
    setShowCreateModal(true);
  };

  const handleEditFromView = (invoice) => {
    // Prefill form data for editing
    const items = (invoice.items && invoice.items.length > 0)
      ? invoice.items
      : (invoice.services || []).map(svc => {
          const srv = availableServices.find(s => s.name === svc) || {};
          return { service: svc, description: '', quantity: 1, price: srv.price || 0 };
        });

    setNewInvoiceData({
      clientName: invoice.clientName || '',
      clientEmail: invoice.clientEmail || '',
      clientPhone: invoice.clientPhone || '',
      items,
      additionalCharges: invoice.additionalCharges || [],
      terms: Array.isArray(invoice.terms) ? invoice.terms : [],
      issueDate: invoice.issueDate || formatDateISO(new Date()),
        quotationId: invoice.quotationId || '',
        eventId: invoice.eventId || '',
      orderType: invoice.orderType || 'own',
      showDescriptionInPdf: invoice.showDescriptionInPdf === true,
      showTermsInPdf: invoice.showTermsInPdf === true,
      showPaymentDetailsInPdf: invoice.showPaymentDetailsInPdf !== false,
      showQrCodeInPdf: invoice.showQrCodeInPdf !== false
    });
      // Prefill reference mode, query inputs and PDF checkbox for editing
      try { setReferenceMode(invoice.eventId ? 'event' : 'quotation'); } catch (e) {}
      try { setUseQuotationData(invoice.showQuotationIdInPdf !== false); } catch (e) {}
      // Set the visible input label for quotation/event search fields so the select shows current value
      try {
        if (invoice.quotationId) {
          const allQ = Array.isArray(quotations) && quotations.length > 0 ? quotations : getDropdownQuotations();
          const found = Array.isArray(allQ) ? allQ.find(x => String(x.id || x.quotationId || x.ref) === String(invoice.quotationId)) : null;
          if (found) setQuotationQuery(String(found.id || found.quotationId || found.ref) + (found.clientName ? ' - ' + found.clientName : ''));
          else setQuotationQuery(String(invoice.quotationId));
        }
      } catch (e) {}
      try {
        if (invoice.eventId) {
          const allE = Array.isArray(events) && events.length > 0 ? events : getDropdownEvents();
          const foundE = Array.isArray(allE) ? allE.find(x => String(x.id || x.quotationId) === String(invoice.eventId)) : null;
          if (foundE) setEventQuery(String(foundE.id || foundE.quotationId) + (foundE.clientName ? ' - ' + foundE.clientName : ''));
          else setEventQuery(String(invoice.eventId));
        }
      } catch (e) {}
    setPreviewInvoiceId(invoice.id);
    setEditingInvoiceId(invoice.id);
    setShowViewModal(false);
    setShowCreateModal(true);
  };

  // Reset create form to empty defaults (only used for Create modal)
  const handleResetCreateForm = () => {
    try {
      setNewInvoiceData(getEmptyInvoiceData());
    } catch (e) {}
    try {
      setPreviewInvoiceId(generateInvoiceId());
    } catch (e) {}
    try {
      // clear any saved draft for the create form
      removeBillingDraftFromSession(makeBillingDraftKey());
    } catch (e) {}
    try { setQuotationQuery(''); } catch (e) {}
    try { setShowQuotationDropdown(false); } catch (e) {}
    try { setHighlightedQuotationIndex(-1); } catch (e) {}
    try { setEventQuery(''); } catch (e) {}
    try { setShowEventDropdown(false); } catch (e) {}
    try { setHighlightedEventIndex(-1); } catch (e) {}
    try { setReferenceMode('quotation'); } catch (e) {}
  };


  const addLineItem = () => {
    setNewInvoiceData({
      ...newInvoiceData,
      items: [{ service: '', description: '', quantity: 1, price: 0 }, ...newInvoiceData.items]
    });
  };

  const removeLineItem = (index) => {
    setNewInvoiceData({
      ...newInvoiceData,
      items: newInvoiceData.items.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...newInvoiceData.items];
    if (field === 'quantity') {
      newItems[index][field] = value === '' ? '' : parseInt(value, 10) || 0;
    } else if (field === 'price') {
      newItems[index][field] = value === '' ? '' : parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    setNewInvoiceData({ ...newInvoiceData, items: newItems });
  };

  const selectService = (index, serviceName) => {
    // Find matching service from available services (if any)
    const service = availableServices.find(s => s.name === serviceName);
    
    // Always update the service name (allows custom service types)
    // If service is found in available services, use its price; otherwise keep existing price
    const newItems = [...newInvoiceData.items];
    const existingPrice = newItems[index]?.price || 0;
    const newPrice = service ? service.price : existingPrice;
    newItems[index] = { ...newItems[index], service: serviceName, price: newPrice };
    setNewInvoiceData({ ...newInvoiceData, items: newItems });
  };

  const calculateSubtotal = () => {
    return newInvoiceData.items.reduce((sum, item) => {
      const q = parseFloat(item.quantity) || 0;
      const p = parseFloat(item.price) || 0;
      return sum + (q * p);
    }, 0);
  };

  const calculateAdditionalCharges = () => {
    return (newInvoiceData.additionalCharges || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  };

  // Open WhatsApp share choice: To Client (send directly) vs To Others (copy template)
  const handleWhatsApp = (invoice) => {
    setShareTarget(invoice);
    setShareChannel('whatsapp');
  };

  const normalizeSharePhone = (raw) => {
    let phone = (raw || '').toString().replace(/\D/g, '');
    if (phone.length === 10) phone = '91' + phone;
    return phone;
  };

  const handleShareToClient = () => {
    if (!shareTarget) return;
    if (shareChannel === 'whatsapp') {
      const rawPhone = shareTarget.clientPhone || shareTarget.client_phone || '';
      const cleaned = normalizeSharePhone(rawPhone);
      if (!cleaned) {
        showToast('No valid phone number available for this invoice.');
        return;
      }
      const message = buildInvoiceWhatsApp({ invoice: shareTarget, company, audience: 'client' });
      try { window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`, '_blank'); } catch (err) { console.error(err); }
    } else {
      const to = (shareTarget.clientEmail || '').trim();
      if (!to) {
        showToast('No email address available for this client.');
        return;
      }
      const { subject, body } = buildInvoiceEmail({ invoice: shareTarget, company, audience: 'client' });
      window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const handleShareOthersViaApp = () => {
    if (!shareTarget) return;
    if (shareChannel === 'whatsapp') {
      const message = buildInvoiceWhatsApp({ invoice: shareTarget, company, audience: 'others' });
      try { window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank'); } catch (err) { console.error(err); }
    } else {
      const { subject, body } = buildInvoiceEmail({ invoice: shareTarget, company, audience: 'others' });
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateAdditionalCharges();
  };

  const addAdditionalCharge = () => {
    setNewInvoiceData({ 
      ...newInvoiceData, 
      additionalCharges: [...(newInvoiceData.additionalCharges || []), { description: '', amount: 0 }] 
    });
  };

  const updateAdditionalCharge = (index, field, value) => {
    const charges = [...(newInvoiceData.additionalCharges || [])];
    if (field === 'amount') {
      charges[index] = { ...charges[index], [field]: value === '' ? '' : (parseFloat(value) || 0) };
    } else {
      charges[index] = { ...charges[index], [field]: value };
    }
    setNewInvoiceData({ ...newInvoiceData, additionalCharges: charges });
  };

  const removeAdditionalCharge = (index) => {
    const charges = (newInvoiceData.additionalCharges || []).filter((_, i) => i !== index);
    setNewInvoiceData({ ...newInvoiceData, additionalCharges: charges });
  };

  // Handle term selection/deselection for billing form
  const handleTermToggle = (termContent) => {
    const currentTerms = newInvoiceData.terms || [];
    if (currentTerms.includes(termContent)) {
      // Remove term
      setNewInvoiceData({ ...newInvoiceData, terms: currentTerms.filter(t => t !== termContent) });
    } else {
      // Add term
      setNewInvoiceData({ ...newInvoiceData, terms: [...currentTerms, termContent] });
    }
  };

  // Select all terms in a group
  const handleSelectGroup = (groupName, selectAll) => {
    const currentTerms = newInvoiceData.terms || [];
    const groupTerms = (groupedPolicies[groupName] || []).map(p => p.content);

    if (selectAll) {
      // Add all group terms that aren't already selected
      const newTerms = [...new Set([...currentTerms, ...groupTerms])];
      setNewInvoiceData({ ...newInvoiceData, terms: newTerms });
    } else {
      // Remove all group terms
      const newTerms = currentTerms.filter(t => !groupTerms.includes(t));
      setNewInvoiceData({ ...newInvoiceData, terms: newTerms });
    }
  };

  // Check if all terms in a group are selected
  const isGroupFullySelected = (groupName) => {
    const currentTerms = newInvoiceData.terms || [];
    const groupTerms = (groupedPolicies[groupName] || []).map(p => p.content);
    return groupTerms.length > 0 && groupTerms.every(t => currentTerms.includes(t));
  };

  // Check if some (but not all) terms in a group are selected
  const isGroupPartiallySelected = (groupName) => {
    const currentTerms = newInvoiceData.terms || [];
    const groupTerms = (groupedPolicies[groupName] || []).map(p => p.content);
    const selectedCount = groupTerms.filter(t => currentTerms.includes(t)).length;
    return selectedCount > 0 && selectedCount < groupTerms.length;
  };

  

  const handleCreateInvoice = async () => {
    // Save any pending new services to database first
    if (pendingNewServices.length > 0) {
      const savedServices = [];
      for (const pendingService of pendingNewServices) {
        // Find the actual price from form items for this service
        const formItem = newInvoiceData.items.find(item => item.service === pendingService.name);
        const actualPrice = formItem ? (parseFloat(formItem.price) || 0) : 0;
        
        const fullService = {
          id: pendingService.id,
          name: pendingService.name,
          category: 'Uncategorized',
          description: '',
          basePrice: actualPrice,
          deliverables: [],
          features: [],
        };
        const saved = await savePendingServiceToDatabase(fullService);
        if (saved) {
          savedServices.push(saved);
        }
      }
      
      // Update availableServices with newly saved services
      if (savedServices.length > 0) {
        setAvailableServices(prev => [...prev, ...savedServices]);
        // Clear pending services as they're now saved
        setPendingNewServices([]);
      }
    }

    // sanitize inputs before validation
    const clientName = (newInvoiceData.clientName || '').toString().replace(/[^A-Za-z\s]/g, '').trim();
    const clientPhone = (newInvoiceData.clientPhone || '').toString().replace(/\D/g, '');
    const clientEmail = (newInvoiceData.clientEmail || '').toString().trim();

    // Basic validation
    if (!clientName) {
      showToast('Please provide a valid client name (letters and spaces only).');
      return;
    }
    if (!/^\d{10}$/.test(clientPhone)) {
      showToast('Please enter a valid 10-digit phone number (numbers only).');
      return;
    }

    if (clientEmail) {
      // simple email validation
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(clientEmail)) {
        showToast('Please provide a valid email address.');
        return;
      }
    }

    if (newInvoiceData.items.every(item => !item.service)) {
      showToast('Please add at least one service item');
      return;
    }

    const totalAmount = calculateTotal();

    const invoice = {
      id: previewInvoiceId || generateInvoiceId(),
      clientName,
      clientEmail,
      clientPhone,
      services: newInvoiceData.items.map(item => item.service).filter(Boolean),
      items: newInvoiceData.items,
      additionalCharges: newInvoiceData.additionalCharges || [],
      terms: newInvoiceData.terms || [],
      amount: totalAmount,
      paid: editingInvoiceId ? (selectedInvoice?.paid || 0) : 0,
      payments: editingInvoiceId ? (selectedInvoice?.payments || []) : [],
      issueDate: newInvoiceData.issueDate,
      status: editingInvoiceId ? (selectedInvoice?.status || 'pending') : 'pending',
      quotationId: newInvoiceData.quotationId || '',
      eventId: newInvoiceData.eventId || '',
      showQuotationIdInPdf: useQuotationData,
      orderType: newInvoiceData.orderType || 'own',
      showDescriptionInPdf: newInvoiceData.showDescriptionInPdf === true,
      showPaymentDetailsInPdf: newInvoiceData.showPaymentDetailsInPdf !== false,
      showQrCodeInPdf: newInvoiceData.showQrCodeInPdf !== false,
      showTermsInPdf: newInvoiceData.showTermsInPdf !== false
    };

    // If authenticated, try to persist to backend
    try {
      const token = session.getToken();
      if (token) {
        if (editingInvoiceId) {
          // Update existing invoice on server
          const resp = await fetch(`${API_BASE_URL}/billing/${encodeURIComponent(editingInvoiceId)}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(invoice),
          });
          if (resp.ok) {
            const updatedInvoice = await resp.json();
            const next = invoices.map(inv => inv.id === editingInvoiceId ? updatedInvoice : inv);
            setInvoices(next);
            try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, next); } catch (e) {}
            showToast("Invoice updated successfully");
          } else {
            showToast('Failed to update invoice on server.');
          }
          setEditingInvoiceId(null);
        } else {
          // Create new invoice on server
          const resp = await fetch(`${API_BASE_URL}/billing`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(invoice),
          });
          if (resp.ok) {
            const serverInvoice = await resp.json();
            setInvoices([serverInvoice, ...invoices]);
            try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, [serverInvoice, ...invoices]); } catch (e) {}
            // creation toast suppressed — notification only
            // Dispatch notification for new invoice if user has enabled it
            if (notifications?.events) {
              try {
                dispatch(addNotification({
                  title: 'Invoice created',
                  message: `${serverInvoice.id} — ${serverInvoice.clientName || 'No client'}`,
                  type: 'info',
                  timestamp: new Date().toISOString(),
                  path: '/billing',
                  data: { openViewId: serverInvoice.id }
                }));
              } catch (e) {}
            }
            try { window.dispatchEvent(new CustomEvent('notifications:refresh')); } catch (e) {}
            try { setTimeout(() => window.dispatchEvent(new CustomEvent('notifications:refresh')), 600); } catch (e) {}
            try { setTimeout(() => window.dispatchEvent(new CustomEvent('notifications:refresh')), 1600); } catch (e) {}
          } else {
            // fallback to client-only behavior
            setInvoices([invoice, ...invoices]);
            try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, [invoice, ...invoices]); } catch (e) {}
          }
        }
      } else {
        // not authenticated — keep client-only behavior
        if (editingInvoiceId) {
          const next = invoices.map(inv => inv.id === editingInvoiceId ? invoice : inv);
          setInvoices(next);
          try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, next); } catch (e) {}
        } else {
          setInvoices([invoice, ...invoices]);
          try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, [invoice, ...invoices]); } catch (e) {}
        }
      }
    } catch (e) {
      // network or other error — fallback
      // eslint-disable-next-line no-console
      console.warn('persist invoice failed', e);
      if (editingInvoiceId) {
        const next = invoices.map(inv => inv.id === editingInvoiceId ? invoice : inv);
        setInvoices(next);
        try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, next); } catch (err) {}
      } else {
        setInvoices([invoice, ...invoices]);
        try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, [invoice, ...invoices]); } catch (err) {}
      }
    }

    setShowCreateModal(false);
    // Clear saved draft for billing create
    try { removeBillingDraftFromSession(makeBillingDraftKey()); } catch (e) {}
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleDeleteInvoice = async (id) => {
    const ok = await confirm('Are you sure you want to delete this invoice?');
    if (!ok) return;
    try {
      const token = session.getToken();
      if (token) {
        await fetch(`${API_BASE_URL}/billing/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('invoice delete failed', e);
    }

    const next = invoices.filter(inv => inv.id !== id);
    setInvoices(next);
    try { storage.setJSONAndSync(STORAGE_KEYS.INVOICES, next); } catch (e) {}
  };

  const handleDownloadInvoice = (invoice) => {
    try {
      const items = Array.isArray(invoice.items) && invoice.items.length > 0 ? invoice.items : [];
      const showDescriptionInPdf = invoice.showDescriptionInPdf === true;

      const itemsRows = items.length > 0 ? items.map(it => `
        <tr>
          <td class="cell-service">${(it.service || '')}</td>
          ${showDescriptionInPdf ? `<td class="cell-desc">${(it.description || '')}</td>` : ''}
          <td style="text-align:center"><mono>${(it.quantity || '')}</mono></td>
          <td style="text-align:right"><mono>₹${(parseFloat(it.price) || 0).toLocaleString('en-IN')}</mono></td>
          <td style="text-align:right"><mono>₹${((parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0)).toLocaleString('en-IN')}</mono></td>
        </tr>`).join('') : '';

      // Additional charges: render as two columns — description (spanning middle columns) and amount
      const charges = Array.isArray(invoice.additionalCharges) ? invoice.additionalCharges : [];
      const chargesRows = charges.length > 0 ? charges.map(ch => `
        <tr>
          <td colspan="${showDescriptionInPdf ? 4 : 3}">${(ch.description || 'Additional charge')}</td>
          <td style="text-align:right"><mono>₹${(parseFloat(ch.amount) || 0).toLocaleString('en-IN')}</mono></td>
        </tr>`).join('') : '';

      const itemsSubtotal = items.reduce((s, it) => s + ((parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0)), 0);
      const chargesTotal = charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
      const computedTotal = itemsSubtotal + chargesTotal;

      // helper: convert number to words (Indian system)
      const numberToWords = (num) => {
        if (num === 0) return 'zero';
        const a = ['','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
        const b = ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];

        const inWords = (n) => {
          let str = '';
          if (n >= 10000000) {
            str += inWords(Math.floor(n/10000000)) + ' crore ';
            n = n % 10000000;
          }
          if (n >= 100000) {
            str += inWords(Math.floor(n/100000)) + ' lakh ';
            n = n % 100000;
          }
          if (n >= 1000) {
            str += inWords(Math.floor(n/1000)) + ' thousand ';
            n = n % 1000;
          }
          if (n >= 100) {
            str += inWords(Math.floor(n/100)) + ' hundred ';
            n = n % 100;
          }
          if (n > 0) {
            if (n < 20) {
              str += a[n] + ' ';
            } else {
              str += b[Math.floor(n/10)] + (n%10 ? ' ' + a[n%10] : '') + ' ';
            }
          }
          return str.trim();
        };

        return inWords(num).replace(/\s+/g,' ').trim();
      };

      

      // prepare amount in words (rupees and paise)
      const totalInteger = Math.floor(computedTotal);
      const paise = Math.round((computedTotal - totalInteger) * 100);
      const rupeesWords = numberToWords(totalInteger) || 'zero';
      const paiseWords = paise ? numberToWords(paise) : '';
      const amountInWords = `${rupeesWords} rupees${paise ? ' and ' + paiseWords + ' paise' : ''} only`;
      const amountInWordsCap = amountInWords.charAt(0).toUpperCase() + amountInWords.slice(1);
      const companyLogoSrc = (typeof company?.logo === 'string' && company.logo.trim()) ? company.logo.trim() : '';
      const companyLogoMode = company?.logoMode === 'landscape' ? 'landscape' : 'square';
      const companyLogoWidth = companyLogoMode === 'landscape' ? '160px' : '64px';
      const companyLogoHeight = companyLogoMode === 'landscape' ? '64px' : '64px';
      const companyBlockWidth = companyLogoMode === 'landscape' ? '360px' : '260px';
      const companyBlockStyle = companyLogoSrc
        ? `width:${companyBlockWidth}; text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:6px`
        : 'text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:6px';

      const html = `<!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Invoice ${invoice.id}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500&display=swap');
          *{box-sizing:border-box} html,body{margin:0;padding:0}
          body{font-family:'Inter','Helvetica Neue',Helvetica,Arial,sans-serif;color:#0F172A;background:#fff;font-size:9.5pt;line-height:1.55;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .pdf-shell{padding:22px 26px 18px}
          .accent-bar{height:4px;background:linear-gradient(90deg,#4F46E5 0%,#06B6D4 50%,#8B5CF6 100%);border-radius:999px;margin-bottom:20px}
          .doc-header{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding-bottom:16px;border-bottom:1px solid #E2E8F0}
          .doc-left{flex:1;min-width:0}
          .doc-eyebrow{font-size:7.5pt;letter-spacing:0.16em;font-weight:700;color:#6366F1;text-transform:uppercase;margin:0 0 6px}
          .doc-title{font-size:21pt;font-weight:800;letter-spacing:-0.03em;color:#0F172A;margin:0;line-height:1}
          .doc-subtitle{display:inline-flex;align-items:center;gap:8px;margin-top:10px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:999px;padding:5px 10px;font-size:7.5pt;font-weight:600;color:#334155;letter-spacing:0.04em}
          .doc-subtitle b{color:#0F172A}
          .meta-grid{margin-top:14px;display:grid;grid-template-columns:1fr;gap:0}
          .meta-row{display:flex;gap:10px;align-items:baseline;padding:5px 0;border-bottom:1px dashed #F1F5F9}
          .meta-row:last-child{border-bottom:none}
          .meta-label{width:92px;flex-shrink:0;font-size:7.3pt;letter-spacing:0.1em;font-weight:700;color:#64748B;text-transform:uppercase}
          .meta-value{font-size:9.2pt;font-weight:600;color:#0F172A;word-break:break-word}
          .meta-value.muted{font-weight:500;color:#334155}
          .company-block{text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:8px;min-width:200px}
          .company-logo{width:64px;height:64px;display:block;border-radius:10px;object-fit:cover;border:1px solid #E2E8F0;box-shadow:0 1px 2px rgba(15,23,42,0.06)}
          .company-logo.landscape{width:148px;height:58px;border-radius:10px}
          .company-name{font-weight:800;font-size:11pt;letter-spacing:-0.02em;color:#0F172A;line-height:1.1}
          .company-contact{font-size:7.8pt;color:#64748B;line-height:1.6;text-align:right}
          .company-gstin{display:inline-block;margin-top:4px;background:#EEF2FF;border:1px solid #E0E7FF;color:#4338CA;font-size:7pt;font-weight:700;letter-spacing:0.06em;padding:3px 8px;border-radius:999px}
          .table-wrap{margin-top:18px;border:1px solid #E2E8F0;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,0.06)}
          table{width:100%;border-collapse:separate;border-spacing:0;margin:0}
          thead th{background:#0F172A;color:#fff;font-size:7.4pt;letter-spacing:0.11em;text-transform:uppercase;font-weight:700;padding:11px 12px;border:none;text-align:left;white-space:nowrap}
          thead th:first-child{border-top-left-radius:12px} thead th:last-child{border-top-right-radius:12px}
          thead th.center{text-align:center} thead th.right{text-align:right}
          tbody td{padding:10px 12px;font-size:9pt;color:#1E293B;border-bottom:1px solid #F1F5F9;vertical-align:top;line-height:1.45}
          tbody tr:last-child td{border-bottom:none} tbody tr:nth-child(even) td{background:#F8FAFC}
          tbody td mono{font-family:'JetBrains Mono',ui-monospace,monospace;font-weight:500}
          .cell-service{font-weight:600;color:#0F172A} .cell-desc{color:#475569;font-size:8.6pt}
          .row-subtotal td{background:#F1F5F9 !important;color:#0F172A !important;font-weight:700 !important;font-size:9pt;border-top:1px solid #E2E8F0}
          .row-charges-head td{background:#EEF2FF !important;color:#4338CA !important;font-weight:700 !important;font-size:7.4pt;letter-spacing:0.1em;text-transform:uppercase;padding:9px 12px !important;border-top:1px solid #E0E7FF;border-bottom:1px solid #E0E7FF}
          .row-total td{background:#0F172A !important;color:#fff !important;font-weight:800 !important;font-size:11pt;padding:13px 12px !important;letter-spacing:-0.01em}
          .amount-chip{margin:12px 0 0;width:100%;max-width:100%;box-sizing:border-box;display:flex;flex-direction:row-reverse;align-items:flex-start;gap:10px;background:#EEF2FF;border:1px solid #E0E7FF;border-right:3px solid #4F46E5;border-left:none;border-radius:10px;padding:10px 14px;text-align:right}
          .amount-chip-icon{width:26px;height:26px;flex-shrink:0;background:#4F46E5;color:#fff;border-radius:999px;display:flex;align-items:center;justify-content:center;font-size:10pt;line-height:1}
          .amount-chip-label{font-size:7pt;letter-spacing:0.1em;font-weight:700;color:#6366F1;text-transform:uppercase;margin:0 0 2px;text-align:right}
          .amount-chip-value{font-size:8.7pt;font-weight:600;color:#1E293B;line-height:1.4;font-style:italic;text-align:right}
          .amount-chip-content{flex:1;text-align:right}
          .terms-wrap{margin-top:18px;page-break-inside:avoid} .terms-card{background:#F8FAFC;border:1px solid #E2E8F0;border-left:3px solid #4F46E5;border-radius:10px;padding:14px 16px}
          .terms-title{margin:0 0 10px;font-size:9pt;font-weight:800;color:#0F172A;letter-spacing:-0.01em;display:flex;align-items:center;gap:8px}
          .terms-title:before{content:"";width:3px;height:14px;background:#4F46E5;border-radius:999px;display:inline-block}
          .terms-list{margin:0;padding-left:16px} .terms-list li{margin:6px 0;color:#334155;font-size:8.6pt;line-height:1.5} .terms-list li::marker{color:#94A3B8}
          .sign-row{margin-top:28px;display:flex;gap:28px} .sign-col{flex:1} .sign-line{height:48px;border-bottom:1.5px solid #CBD5E1;margin-bottom:10px}
          .sign-label{font-size:7.3pt;letter-spacing:0.12em;font-weight:700;color:#64748B;text-transform:uppercase;margin:0}
          .sign-name{margin-top:4px;font-size:9.2pt;font-weight:700;color:#0F172A} .sign-meta{font-size:7.8pt;color:#64748B;margin-top:2px}
          .payment-outer{margin-top:18px;page-break-inside:avoid} .payment-wrap{display:flex;gap:14px;align-items:stretch;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:12px;padding:14px}
          .payment-card{flex:1;background:#fff;border:1px solid #E2E8F0;border-radius:10px;padding:14px;box-shadow:0 1px 2px rgba(15,23,42,0.05)}
          .payment-card.qr{width:210px;flex:0 0 210px;text-align:center;display:flex;flex-direction:column;align-items:center;justify-content:center}
          .payment-card-title{margin:0 0 10px;font-size:8.5pt;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#0F172A;display:flex;align-items:center;gap:7px}
          .payment-card-title .dot{width:7px;height:7px;border-radius:999px;background:#4F46E5;display:inline-block}
          .payment-card.qr .payment-card-title{justify-content:center}
          .bank-row{display:flex;gap:8px;padding:5px 0;border-bottom:1px solid #F8FAFC;font-size:8.6pt}
          .bank-row:last-child{border-bottom:none} .bank-label{width:86px;flex-shrink:0;font-weight:700;color:#64748B;font-size:7.6pt;letter-spacing:0.06em;text-transform:uppercase;padding-top:1px}
          .bank-value{flex:1;font-weight:600;color:#0F172A;word-break:break-word} mono{font-family:'JetBrains Mono',ui-monospace,monospace;font-weight:500}
          .bank-note{margin-top:10px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:8px 10px;font-size:7.8pt;color:#92400E;line-height:1.5} .bank-note b{color:#78350F}
          .upi-id{margin:0 0 8px;background:#F8FAFC;border:1px dashed #CBD5E1;border-radius:8px;padding:7px 10px;font-size:8.4pt;font-weight:700;color:#0F172A;word-break:break-all}
          .upi-qr{width:132px;height:132px;border-radius:10px;object-fit:cover;border:1px solid #E2E8F0;display:block}
          .upi-hint{margin-top:8px;font-size:7.5pt;color:#94A3B8;letter-spacing:0.02em}
          .doc-footer{margin-top:18px;text-align:center;font-size:7pt;color:#94A3B8;letter-spacing:0.06em;border-top:1px solid #F1F5F9;padding-top:10px}
        </style>
      </head>
      <body>
        <div class="pdf-shell">
          <div class="accent-bar"></div>
          <div class="doc-header">
            <div class="doc-left">
              <p class="doc-eyebrow">Invoice</p>
              <h1 class="doc-title">${invoice.id}</h1>
              <div class="doc-subtitle">Issued &nbsp;<b>${formatDateDisplay(invoice.issueDate) || "—"}</b></div>
              <div class="meta-grid">
                <div class="meta-row"><span class="meta-label">Client</span><span class="meta-value">${invoice.clientName || "—"}</span></div>
                <div class="meta-row"><span class="meta-label">Phone</span><span class="meta-value muted">${invoice.clientPhone || "—"}</span></div>
                <div class="meta-row"><span class="meta-label">Email</span><span class="meta-value muted">${invoice.clientEmail || "—"}</span></div>
                ${invoice.showQuotationIdInPdf !== false ? (invoice.eventId ? `<div class="meta-row"><span class="meta-label">Event ID</span><span class="meta-value"><mono>${invoice.eventId}</mono></span></div>` : (invoice.quotationId ? `<div class="meta-row"><span class="meta-label">Quotation ID</span><span class="meta-value"><mono>${invoice.quotationId}</mono></span></div>` : '')) : ''}
              </div>
            </div>
            <div class="company-block" style="${companyBlockStyle}">
              ${companyLogoSrc ? `<img src="${companyLogoSrc}" alt="Company Logo" class="company-logo ${companyLogoMode === 'landscape' ? 'landscape' : ''}" style="border-radius:10px" />` : ''}
              <div class="company-name">${company?.name || 'Company Name'}</div>
              <div class="company-contact">
                ${company?.phone ? `<span><strong>Phone</strong> ${company.phone}</span><br/>` : ''}
                ${company?.email ? `<span><strong>Email</strong> ${company.email}</span>` : ''}
                ${company?.address ? `<br/><span>${company.address}</span>` : ''}
              </div>
              ${company?.gstin ? `<span class="company-gstin">GSTIN ${company.gstin}</span>` : ''}
            </div>
          </div>

          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  ${showDescriptionInPdf ? '<th>Description</th>' : ''}
                  <th class="center">Qty</th>
                  <th class="right">Unit</th>
                  <th class="right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
                <tr class="row-subtotal">
                  <td colspan="${showDescriptionInPdf ? 4 : 3}" style="text-align:right">Subtotal</td>
                  <td style="text-align:right"><mono>₹${itemsSubtotal.toLocaleString('en-IN')}</mono></td>
                </tr>
                ${charges.length > 0 ? `
                <tr class="row-charges-head">
                  <td colspan="${showDescriptionInPdf ? 5 : 4}">Additional Charges</td>
                </tr>
                ${chargesRows}
                ` : ''}
                <tr class="row-total">
                  <td colspan="${showDescriptionInPdf ? 4 : 3}" style="text-align:right">Total</td>
                  <td style="text-align:right"><mono>₹${computedTotal.toLocaleString('en-IN')}</mono></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="amount-chip">
            <div class="amount-chip-icon">₹</div>
            <div class="amount-chip-content">
              <div class="amount-chip-label">Amount in words</div>
              <div class="amount-chip-value">${amountInWordsCap}</div>
            </div>
          </div>

          ${(invoice.showTermsInPdf !== false && Array.isArray(invoice.terms) && invoice.terms.length > 0) ? `
          <div class="terms-wrap">
            <div class="terms-card">
              <h3 class="terms-title">Terms & Conditions</h3>
              <ul class="terms-list">
                ${invoice.terms.map(term => `<li>${term}</li>`).join('')}
              </ul>
            </div>
          </div>
          ` : ''}

          <!-- Signature blocks -->
          <div class="sign-row">
            <div class="sign-col">
              <div class="sign-line"></div>
              <p class="sign-label">Client Signature</p>
              <div class="sign-name">${invoice.clientSignature || ''}</div>
            </div>
            <div class="sign-col" style="text-align:right">
              <div class="sign-line"></div>
              <p class="sign-label" style="text-align:right">Authorized Signatory</p>
              <div class="sign-name">${company?.name || 'Company Name'}</div>
              ${company?.phone ? `<div class="sign-meta">Phone: ${company.phone}</div>` : ''}
              ${company?.email ? `<div class="sign-meta">Email: ${company.email}</div>` : ''}
            </div>
          </div>

          ${(invoice.showPaymentDetailsInPdf !== false || invoice.showQrCodeInPdf !== false) ? `
          <div class="payment-outer">
            <div class="payment-wrap">
            ${invoice.showPaymentDetailsInPdf !== false ? `
            <div class="payment-card">
              <h3 class="payment-card-title"><span class="dot"></span> Payment Details</h3>
              <div class="bank-grid">
                ${bank?.bankName ? `<div class="bank-row"><span class="bank-label">Bank</span><span class="bank-value">${bank.bankName}</span></div>` : ''}
                ${bank?.accountHolder ? `<div class="bank-row"><span class="bank-label">Account</span><span class="bank-value">${bank.accountHolder}</span></div>` : ''}
                ${bank?.accountNumber ? `<div class="bank-row"><span class="bank-label">A/C No.</span><span class="bank-value"><mono>${bank.accountNumber}</mono></span></div>` : ''}
                ${bank?.branch ? `<div class="bank-row"><span class="bank-label">Branch</span><span class="bank-value">${bank.branch}</span></div>` : ''}
                ${bank?.ifsc ? `<div class="bank-row"><span class="bank-label">IFSC</span><span class="bank-value"><mono>${bank.ifsc}</mono></span></div>` : ''}
                ${bank?.address ? `<div class="bank-row"><span class="bank-label">Address</span><span class="bank-value">${bank.address}</span></div>` : ''}
              </div>
              <div class="bank-note"><b>Note:</b> After the successful payment, please inform us with the Invoice ID (${invoice.id}) as the payment reference.</div>
            </div>
            ` : ''}

            ${invoice.showQrCodeInPdf !== false ? `
            <div class="payment-card qr">
              <h3 class="payment-card-title"><span class="dot" style="background:#10B981"></span> UPI Payment</h3>
              ${bank?.upiId ? `<div class="upi-id">${bank.upiId}</div>` : ''}
              ${bank?.upiQr ? `<img src="${bank.upiQr}" alt="UPI QR" class="upi-qr" />` : `<div style="width:132px;height:132px;border:1px dashed #CBD5E1;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#94A3B8;font-size:7.5pt">QR not set</div>`}
              <div class="upi-hint">Scan or use the UPI ID to pay</div>
            </div>
            ` : ''}
            </div>
          </div>
          ` : ''}
          <div class="doc-footer">Thank you for your trust &nbsp;·&nbsp; This is a computer-generated document</div>
        </div>
      </body>
      </html>`;

      const win = window.open('', '_blank');
      if (!win) {
        showToast('Popup blocked. Please allow popups for this site to download the PDF.');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      // Give the new window a moment to render before printing
      setTimeout(() => { try { win.print(); } catch (e) { console.error(e); } }, 500);
    } catch (e) {
      console.error('download error', e);
      showToast('Unable to prepare the invoice for download.');
    }
  };

  const handleSendEmail = (invoice) => {
    // Open choice dialog: To Client (send directly) vs To Others (copy template)
    setShareTarget(invoice);
    setShareChannel('email');
  };

  const handleSubmitPayment = () => {
    if (selectedInvoice) {
      const amt = parseFloat(paymentData.amount) || 0;
      const newPaidAmount = (selectedInvoice.paid || 0) + amt;
      const newStatus = newPaidAmount >= selectedInvoice.amount ? 'paid' :
                       newPaidAmount > 0 ? 'partial' : selectedInvoice.status;

      const methodLabel = formatPaymentMethod(paymentData.paymentMethod);
      const paymentRecord = {
        amount: amt,
        date: paymentData.paymentDate,
        method: methodLabel,
        reference: paymentData.reference || '',
        notes: paymentData.notes || '',
        recordedAt: new Date().toISOString()
      };

      const updatedInvoices = invoices.map(inv =>
        inv.id === selectedInvoice.id
              ? {
              ...inv,
              paid: newPaidAmount,
              status: newStatus,
              paymentMethod: methodLabel,
              payments: [...(inv.payments || []), paymentRecord]
            }
          : inv
      );

      setInvoices(updatedInvoices);

      // update selectedInvoice in state so the UI reflects the new payment
      setSelectedInvoice(prev => prev ? ({ ...prev, paid: newPaidAmount, status: newStatus, paymentMethod: methodLabel, payments: [...(prev.payments || []), paymentRecord] }) : prev);

      // Persist change to server when authenticated
      (async () => {
        try {
          const token = session.getToken();
          if (token) {
            const payload = {
              paid: newPaidAmount,
              status: newStatus,
              paymentMethod: methodLabel,
              payments: updatedInvoices.find(i => i.id === selectedInvoice.id).payments || []
            };
            await fetch(`${API_BASE_URL}/billing/${encodeURIComponent(selectedInvoice.id)}`, {
              method: 'PUT',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
          }
        } catch (e) {
          // Persist failed - UI already updated optimistically. Notify user.
          // eslint-disable-next-line no-console
          console.warn('Failed to persist payment to server', e);
          showToast('Payment recorded locally but failed to save to server.');
        }
      })();

      setShowPaymentModal(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleColumn = (columnKey) => {
    // NOTE: never dispatch inside a setState updater — updaters must be pure.
    const next = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    };
    setVisibleColumns(next);
    dispatch(updateColumnPreferences({ billing: next }));
    dispatch(saveColumnPreferences({ billing: next }));
  };

  const toggleAllColumns = (checked) => {
    const newVisibleColumns = {};
    columnOptions.forEach(column => {
      newVisibleColumns[column.key] = checked;
    });
    const next = { ...BILLING_COLUMN_DEFAULTS, ...newVisibleColumns };
    setVisibleColumns(next);
    dispatch(updateColumnPreferences({ billing: next }));
    dispatch(saveColumnPreferences({ billing: next }));
  };

  const isAllColumnsSelected = () => {
    return columnOptions.every(column => visibleColumns[column.key]);
  };

  const columnOptions = [
    { key: 'id', label: t('billing.colInvoiceId') },
    { key: 'eventId', label: t('billing.colEventId') },
    { key: 'quotationId', label: t('billing.colQuotId') },
    { key: 'orderType', label: t('common.orderType') },
    { key: 'services', label: t('common.services') },
    { key: 'amount', label: t('common.amount') },
    { key: 'paid', label: t('common.paid') },
    { key: 'balance', label: t('billing.colBalance') },
    { key: 'status', label: t('common.status') }
  ];

  const getFilteredInvoices = (opts = {}) => {
    const skipStatFilter = opts?.skipStatFilter === true;
    const skipStatusFilter = opts?.skipStatusFilter === true;
    const q = (searchQuery || '').toString().trim().toLowerCase();
    let filtered = invoices.filter(invoice => {
        // Date range filtering (issueDate in YYYY-MM-DD)
        if (fromDate || toDate) {
          const invDate = invoice.issueDate ? new Date(invoice.issueDate) : null;
          if (!invDate) return false; // exclude when range applied
          if (fromDate) {
            const f = new Date(fromDate);
            f.setHours(0,0,0,0);
            if (invDate < f) return false;
          }
          if (toDate) {
            const t = new Date(toDate);
            t.setHours(23,59,59,999);
            if (invDate > t) return false;
          }
        }

      const clientName = (invoice?.clientName || '').toString().toLowerCase();
      const id = (invoice?.id || '').toString().toLowerCase();
      const issueDate = (invoice?.issueDate || '').toString().toLowerCase();
      const quotationId = (invoice?.quotationId || '').toString().toLowerCase();

      // services may be stored in invoice.services (array) or in items
      const servicesArr = Array.isArray(invoice.services)
        ? invoice.services
        : (Array.isArray(invoice.items) ? invoice.items.map(it => it.service || '') : []);
      const servicesText = servicesArr.join(' ').toLowerCase();

      // Amount numeric comparison helper: allow queries like "5000" or "5,000" or "₹5000"
      const rawAmount = invoice.amount;
      const amountNum = (rawAmount === undefined || rawAmount === null) ? NaN : Number(rawAmount);

      let matchesSearch = false;
      if (!q) {
        matchesSearch = true;
      } else {
        matchesSearch = (
          clientName.includes(q) ||
          id.includes(q) ||
          issueDate.includes(q) ||
          quotationId.includes(q) ||
          servicesText.includes(q)
        );

        // If not matched yet, try numeric match against amount
        if (!matchesSearch) {
          const numericQuery = q.replace(/[^0-9.]/g, '');
          if (numericQuery) {
            const qNum = parseFloat(numericQuery);
            if (!Number.isNaN(qNum) && !Number.isNaN(amountNum)) {
              // exact match or same when rounded to 2 decimals
              if (Math.abs(amountNum - qNum) < 0.001) {
                matchesSearch = true;
              } else {
                // also match if amount contains the digits (helps partial amounts)
                if (String(amountNum).toLowerCase().includes(numericQuery)) matchesSearch = true;
              }
            }
          }
        }
      }

      const matchesStatus = skipStatusFilter || filterStatus === 'all' || invoice.status === filterStatus ||
        (filterStatus === 'pending' && invoice.status === 'overdue');
      const matchesOrderType = filterOrderType === 'all' || (invoice.orderType === filterOrderType);
      const matchesStat = skipStatFilter || !statFilter || (() => {
        if (statFilter === 'paid') return invoice.status === 'paid';
        if (statFilter === 'partial') return invoice.status === 'partial';
        if (statFilter === 'pending') return invoice.status === 'pending' || invoice.status === 'overdue';
        return true;
      })();

      return matchesSearch && matchesStatus && matchesOrderType && matchesStat;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        // 'dueDate' removed
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'date':
          comparison = new Date(a.issueDate || a.createdAt || 0) - new Date(b.issueDate || b.createdAt || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'partial': return '#0ea5e9';
      case 'pending': return '#fbbf24';
      case 'overdue': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <FaCheck />;
      case 'partial': return <FaClock />;
      case 'pending': return <FaClock />;
      case 'overdue': return <FaExclamationTriangle />;
      default: return <FaClock />;
    }
  };

  // Normalize/display payment method labels (accepts codes or labels)
  const formatPaymentMethod = (m) => {
    // Map various possible stored values to one of the allowed methods
    // Allowed method labels: 'Bank Transfer', 'UPI', 'Cash', 'Cheque'
    if (!m) return 'Bank Transfer';
    const key = String(m).toLowerCase().trim();
    if (key.includes('upi')) return 'UPI';
    if (key.includes('cash')) return 'Cash';
    if (key.includes('cheque')) return 'Cheque';
    if (key.includes('bank') || key.includes('transfer') || key.includes('account')) return 'Bank Transfer';
    // Fallback to Bank Transfer to keep only allowed labels
    return 'Bank Transfer';
  };

  // Keep dropdown filter and stat cards directly proportional (bidirectional sync).
  // Total cards <-> 'all' (statFilter null); pending/paid/partial cards <-> same status.
  const toggleStatFilter = (filter) => {
    setStatFilter((prev) => {
      const next = prev === filter ? null : filter;
      setFilterStatus(next === null ? 'all' : next);
      return next;
    });
  };

  const resetStatFilter = () => {
    setStatFilter(null);
    setFilterStatus('all');
  };

  const handleFilterStatusChange = (status) => {
    setFilterStatus(status);
    if (status === 'all') setStatFilter(null);
    else if (status === 'pending' || status === 'paid' || status === 'partial') setStatFilter(status);
    else setStatFilter(null);
  };

  // Stats ignore both dropdown + card status filters so selecting a card
  // never changes the other stat counts (still respects search/order/date).
  const statsSource = getFilteredInvoices({ skipStatFilter: true, skipStatusFilter: true });
  const stats = {
    total: statsSource.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0),
    // Paid = received/completed amount (only fully paid invoices)
    paid: statsSource
      .filter((inv) => inv.status === 'paid')
      .reduce((sum, inv) => {
        const paidVal = parseFloat(inv.paid);
        if (!Number.isNaN(paidVal) && inv.paid !== null && inv.paid !== '' && inv.paid !== undefined) return sum + paidVal;
        return sum + (parseFloat(inv.amount) || 0);
      }, 0),
    // Pending = balance amount (amount - paid) for pending/overdue invoices
    pending: statsSource.reduce((sum, inv) =>
      inv.status === 'pending' || inv.status === 'overdue'
        ? sum + Math.max((parseFloat(inv.amount) || 0) - (parseFloat(inv.paid) || 0), 0)
        : sum, 0
    ),
    // Partial = balance amount (amount - paid) for partial invoices
    partial: statsSource.filter(inv => inv.status === 'partial').reduce((sum, inv) =>
      sum + Math.max((parseFloat(inv.amount) || 0) - (parseFloat(inv.paid) || 0), 0), 0)
  };

  const filteredInvoices = getFilteredInvoices();
  const invoicesForStats = statsSource;

  // Load/dropdown helper: populate dropdownQuotations state from server
  const loadDropdownQuotations = async () => {
    setDropdownQuotationsError('');
    try {
      // Try to fetch from server via storage helper
      const remote = await storage.syncFromServer(STORAGE_KEYS.QUOTATIONS);
      if (Array.isArray(remote)) {
        setDropdownQuotations(remote);
        return;
      }
      setDropdownQuotations([]);
    } catch (e) {
      setDropdownQuotationsError('Could not load quotations');
      console.warn('loadDropdownQuotations: failed', e);
      setDropdownQuotations([]);
    }
  };

  // Simple getter used in JSX to keep previous code paths working
  const getDropdownQuotations = () => dropdownQuotations || [];

  // Load/dropdown helper: populate dropdownEvents state from server
  const loadDropdownEvents = async () => {
    setDropdownEventsError('');
    try {
      // Try to fetch from server via storage helper
      const remote = await storage.syncFromServer(STORAGE_KEYS.EVENTS);
      if (Array.isArray(remote)) {
        setDropdownEvents(remote);
        return;
      }
      setDropdownEvents([]);
    } catch (e) {
      setDropdownEventsError('Could not load events');
      console.warn('loadDropdownEvents: failed', e);
      setDropdownEvents([]);
    }
  };

  const refreshEventsFromServer = async () => {
    try {
      const remote = await storage.syncFromServer(STORAGE_KEYS.EVENTS);
      setEvents(Array.isArray(remote) ? remote : []);
    } catch (e) {
      setEvents([]);
    }
  };

  // Simple getter used in JSX to keep previous code paths working
  const getDropdownEvents = () => dropdownEvents || [];

  // Pagination logic
  const totalPages = Math.ceil(filteredInvoices.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, statFilter, filterOrderType, fromDate, toDate, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleExportInvoices = async () => {
    try {
      const confirmed = await confirm('Export invoices to CSV?', {
        title: 'Export Invoices',
        confirmText: 'Export',
        cancelText: 'Cancel',
        showWarning: false
      });
      if (!confirmed) return;
      const rows = (Array.isArray(filteredInvoices) && filteredInvoices.length > 0) ? filteredInvoices : invoices;
      if (!rows || rows.length === 0) {
        showToast('No invoices to export');
        return;
      }

      // Build headers: S/N, then place Invoice ID and Quotation ID (if visible),
      // then Client Name/Phone/Email, then remaining visible columns in columnOptions order
      const headers = ['S/N'];
      if (visibleColumns.id) headers.push('Invoice ID');
      if (visibleColumns.quotationId) headers.push('Quotation ID');
      if (visibleColumns.eventId) headers.push('Event ID');
      headers.push('Client Name', 'Client Phone', 'Client Email');

      // Append remaining visible columns from columnOptions, skipping id, quotationId and eventId
      columnOptions.forEach(col => {
        if (col.key === 'id' || col.key === 'quotationId' || col.key === 'eventId') return;
        if (visibleColumns[col.key]) headers.push(col.label);
      });

      const esc = (v) => {
        const s = v === undefined || v === null ? '' : String(v);
        return '"' + s.replace(/"/g, '""') + '"';
      };

      const lines = rows.map((inv, idx) => {
        // Start with S/N, then invoice/quotation IDs (if visible), then client info
        const base = [idx + 1];
        if (visibleColumns.id) base.push(inv.id || '');
        if (visibleColumns.quotationId) base.push(inv.quotationId || '');
        if (visibleColumns.eventId) base.push(inv.eventId || '');
        base.push(inv.clientName || '', inv.clientPhone || '', inv.clientEmail || '');

        // Append remaining visible columns in columnOptions order (skip id, quotationId & eventId)
        columnOptions.forEach(col => {
          if (col.key === 'id' || col.key === 'quotationId' || col.key === 'eventId') return;
          if (!visibleColumns[col.key]) return;
          switch (col.key) {
            case 'orderType':
              base.push(inv.orderType === 'sub' ? 'Sub Order' : 'Own Order');
              break;
            case 'eventId':
              base.push(inv.eventId || '');
              break;
            case 'services':
              base.push(Array.isArray(inv.services) ? inv.services.join('; ') : (inv.services || ''));
              break;
            case 'amount':
              base.push(parseFloat(inv.amount) || 0);
              break;
            case 'paid':
              base.push(parseFloat(inv.paid) || 0);
              break;
            case 'balance': {
              const amt = parseFloat(inv.amount) || 0;
              const paid = parseFloat(inv.paid) || 0;
              base.push(amt - paid);
              break;
            }
            case 'status':
              base.push(inv.status || '');
              break;
            default:
              base.push('');
          }
        });

        return base.map(esc).join(',');
      });

      const csv = [headers.map(esc).join(','), ...lines].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = formatDateISO(new Date());
      a.download = `invoices_export_${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('export error', e);
      showToast('Unable to export invoices');
    }
  };

  return (
    <div className="billing">
      {/* Header */}
      <PageHero
        icon={<FaReceipt />}
        title={t('pages.billing')}
        subtitle={t('pages.billingSubtitle')}
        actions={
          <>
            <button
              className="page-hero__btn"
              onClick={handleExportInvoices}
              title="Export Invoices"
            >
              <FaShareSquare /> {t('common.export')}
            </button>
            <button
              className="page-hero__btn"
              onClick={handleOpenCreate}
              title="Create New Bill"
            >
              <FaPlus /> {t('billing.newBill')}
            </button>
          </>
        }
      />

      {/* Stats */}
      <div className="billing__stats">
        <div className={`billing__stat-card${!statFilter ? ' billing__stat-card--active' : ''}`} onClick={() => resetStatFilter()} style={{ cursor: 'pointer' }}>
          <div className="billing__stat-icon billing__stat-icon--indigo">
            <FaFileInvoiceDollar className="billing__stat-svg--indigo" />
          </div>
          <div className="billing__stat-info">
            <h3>₹{stats.total.toLocaleString('en-IN')}</h3>
            <p>{t('billing.statTotalAmount')}</p>
          </div>
        </div>
        <div className={`billing__stat-card${statFilter === 'partial' ? ' billing__stat-card--active' : ''}`} onClick={() => toggleStatFilter('partial')} style={{ cursor: 'pointer' }}>
          <div className="billing__stat-icon billing__stat-icon--blue">
            <FaClock className="billing__stat-svg--blue" />
          </div>
          <div className="billing__stat-info">
            <h3>₹{stats.partial.toLocaleString('en-IN')}</h3>
            <p>{t('billing.statusPartial')}</p>
          </div>
        </div>
        <div className={`billing__stat-card${statFilter === 'pending' ? ' billing__stat-card--active' : ''}`} onClick={() => toggleStatFilter('pending')} style={{ cursor: 'pointer' }}>
          <div className="billing__stat-icon billing__stat-icon--amber">
            <FaClock className="billing__stat-svg--amber" />
          </div>
          <div className="billing__stat-info">
                <h3>₹{stats.pending.toLocaleString('en-IN')}</h3>
            <p>{t('billing.statPending')}</p>
          </div>
        </div>
        <div className={`billing__stat-card${statFilter === 'paid' ? ' billing__stat-card--active' : ''}`} onClick={() => toggleStatFilter('paid')} style={{ cursor: 'pointer' }}>
          <div className="billing__stat-icon billing__stat-icon--green">
            <FaCheckCircle className="billing__stat-svg--green" />
          </div>
          <div className="billing__stat-info">
                <h3>₹{stats.paid.toLocaleString('en-IN')}</h3>
            <p>{t('billing.statPaid')}</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="billing__toolbar">
          <div className="billing__search">
            <FaSearch />
            <input
              type="text"
              placeholder={t('billing.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        <div className="billing__toolbar-right">
          <div className="billing__date-range">
            <label>{t('common.from')}</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <label>{t('common.toDate')}</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="billing__filter">
            <FaFilter />
            <CustomSelect
              value={filterStatus}
              onChange={handleFilterStatusChange}
              options={[
                { value: 'all', label: t('common.allStatus') },
                { value: 'pending', label: t('common.pending') },
                { value: 'partial', label: t('billing.statusPartial') },
                { value: 'paid', label: t('common.paid') }
              ]}
            />
          </div>
          <div className="billing__filter">
            <FaFilter />
            <CustomSelect
              value={filterOrderType}
              onChange={setFilterOrderType}
              options={[
                { value: 'all', label: t('common.allOrders') },
                { value: 'own', label: t('common.own') },
                { value: 'sub', label: t('common.sub') }
              ]}
            />
          </div>
          {viewMode === 'table' && (
            <div className="billing__column-selector">
              <button
                ref={billingBtnRef}
                className="billing__column-btn"
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                title="Column Preferences"
              >
                <FaColumns />
              </button>
              {showColumnMenu && (
                <div ref={billingMenuRef} className="billing__column-menu">
                  <div className="billing__column-menu-header">
                    <span>{t('common.showColumns')}</span>
                    <button onClick={() => setShowColumnMenu(false)}>
                      <FaTimes />
                    </button>
                  </div>
                  <div className="billing__column-menu-items">
                    <label className="billing__column-menu-item billing__column-menu-item--select-all">
                      <input
                        type="checkbox"
                        checked={isAllColumnsSelected()}
                        onChange={(e) => toggleAllColumns(e.target.checked)}
                      />
                      {isAllColumnsSelected() ? <FaCheckSquare /> : <FaSquare />}
                      <span><strong>{t('common.selectAll')}</strong></span>
                    </label>
                    <div className="billing__column-menu-divider"></div>
                    {columnOptions.map(column => (
                      <label key={column.key} className="billing__column-menu-item">
                        <input
                          type="checkbox"
                          checked={visibleColumns[column.key]}
                          onChange={() => toggleColumn(column.key)}
                        />
                        {visibleColumns[column.key] ? <FaCheckSquare /> : <FaSquare />}
                        <span>{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="billing__view-toggle">
            <button
              className={`billing__view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaList />
            </button>
            <button
              className={`billing__view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FaThLarge />
            </button>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="billing__content">
        {filteredInvoices.length === 0 ? (
          <div className="billing__empty">
            <FaReceipt />
            <h3>{t('billing.emptyTitle')}</h3>
            <p>{t('billing.emptyHint')}</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="billing__table-container">
            <table className="billing__table">
              <thead>
                <tr>
                  <th className="billing__col-no">{t('common.noCol')}</th>
                  {visibleColumns.id && <th className="billing__col-id">{t('billing.colInvoiceId')}</th>}
                  {visibleColumns.quotationId && <th className="billing__col-quotation-id">{t('billing.colQuotId')}</th>}
                  {visibleColumns.eventId && <th className="billing__col-event-id">{t('billing.colEventId')}</th>}
                  {visibleColumns.orderType && <th className="billing__col-order-type">{t('common.orderType')}</th>}
                  <th 
                    className="billing__col-client billing__sortable-header"
                    onClick={() => handleSort('name')}
                    
                  >
                    {t('common.client')} {' '}
                    {sortBy === 'name' ? (
                      sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                    ) : (
                      <FaSort className="billing__sort-icon--muted" />
                    )}
                  </th>
                  {visibleColumns.services && <th className="billing__col-services">{t('common.services')}</th>}
                  {visibleColumns.amount && (
                    <th 
                      className="billing__col-amount billing__sortable-header"
                      onClick={() => handleSort('amount')}
                      
                    >
                      {t('common.amount')} {' '}
                      {sortBy === 'amount' ? (
                        sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                      ) : (
                        <FaSort className="billing__sort-icon--muted" />
                      )}
                    </th>
                  )}
                  {visibleColumns.paid && <th className="billing__col-paid">{t('common.paid')}</th>}
                  {visibleColumns.balance && <th className="billing__col-balance">{t('billing.colBalance')}</th>}
                  {/* dueDate column removed */}
                  {visibleColumns.status && <th className="billing__col-status">{t('common.status')}</th>}
                  <th className="billing__col-actions">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvoices.map((invoice, idx) => (
                  <tr
                    key={invoice.id}
                    onClick={() => handleViewInvoice(invoice)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleViewInvoice(invoice); }}
                  >
                    <td className="billing__col-no">{idx + 1}</td>
                    {visibleColumns.id && (
                      <td className="billing__col-id">
                        <span className="billing__id"><Highlight text={invoice.id} query={searchQuery} /></span>
                      </td>
                    )}
                    {visibleColumns.quotationId && (
                      <td className="billing__col-quotation-id">
                        <span className="billing__quotation-id"><Highlight text={invoice.quotationId || ''} query={searchQuery} /></span>
                      </td>
                    )}
                    {visibleColumns.eventId && (
                      <td className="billing__col-event-id">
                        <span className="billing__event-id"><Highlight text={(invoice.eventId || '')} query={searchQuery} /></span>
                      </td>
                    )}
                    {visibleColumns.orderType && (
                      <td className="billing__col-order-type">
                        <span className={`billing__order-type-badge billing__order-type-badge--${invoice.orderType === 'sub' ? 'sub' : 'own'}`}>
                          {invoice.orderType === 'sub' ? 'Sub' : 'Own'}
                        </span>
                      </td>
                    )}
                    <td className="billing__col-client">
                      <div className="billing__client">
                        <div className="billing__client-info">
                          <div className="billing__client-name"><Highlight text={invoice.clientName} query={searchQuery} /></div>
                          <div className="billing__client-phone"><Highlight text={invoice.clientPhone} query={searchQuery} /></div>
                          <div className="billing__client-email"><Highlight text={invoice.clientEmail} query={searchQuery} /></div>
                        </div>
                      </div>
                    </td>
                    {visibleColumns.services && (
                      <td className="billing__col-services">
                        <ServiceTags
                          services={invoice.services || []}
                          containerClass="billing__services"
                          tagClass="billing__service-tag"
                          moreClass="billing__service-more"
                          maxVisible={4}
                        />
                      </td>
                    )}
                    {visibleColumns.amount && (
                      <td className="billing__col-amount">
                        <span className="billing__amount">
                          ₹{invoice.amount.toLocaleString('en-IN')}
                        </span>
                      </td>
                    )}
                    {visibleColumns.paid && (
                      <td className="billing__col-paid">
                        <span className="billing__paid">
                          ₹{invoice.paid.toLocaleString('en-IN')}
                        </span>
                      </td>
                    )}
                    {visibleColumns.balance && (
                      <td className="billing__col-balance">
                        <span className="billing__balance">
                          ₹{(invoice.amount - invoice.paid).toLocaleString('en-IN')}
                        </span>
                      </td>
                    )}
                    {/* dueDate column removed */}
                    {visibleColumns.status && (
                      <td className="billing__col-status">
                        <span className={`billing__status billing__status--${invoice.status}`}>
                          {getStatusIcon(invoice.status)}
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                    )}
                    <td className="billing__col-actions">
                      <div className="billing__actions">
                        <button
                          className="billing__action-btn billing__action-btn--view"
                          onClick={(e) => { e.stopPropagation(); handleViewInvoice(invoice); }}
                          title="View"
                        >
                          <FaEye />
                        </button>
                        {invoice.status !== 'paid' && (
                          <button
                            className="billing__action-btn billing__action-btn--payment"
                            onClick={(e) => { e.stopPropagation(); handleRecordPayment(invoice); }}
                            title="Record Payment"
                          >
                            <FaMoneyBillWave />
                          </button>
                        )}
                        <button
                          className="billing__action-btn billing__action-btn--download"
                          onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(invoice); }}
                          title="Download PDF"
                        >
                          <FaDownload />
                        </button>
                        <button
                          className="billing__action-btn billing__action-btn--whatsapp"
                          title="Send WhatsApp"
                          onClick={(e) => { e.stopPropagation(); handleWhatsApp(invoice); }}
                        >
                          <FaWhatsapp />
                        </button>
                        <button
                          className="billing__action-btn billing__action-btn--email"
                          title="Send Email"
                          onClick={(e) => { e.stopPropagation(); handleSendEmail(invoice); }}
                        >
                          <FaEnvelope />
                        </button>
                        <button
                          className="billing__action-btn billing__action-btn--delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteInvoice(invoice.id); }}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="billing__grid">
            {paginatedInvoices.map((invoice, idx) => (
              <div key={invoice.id} className="billing__card">
                <div className="billing__card-header">
                  <div className="billing__card-header-row billing__card-header-row--top">
                    <span className="billing__card-no">#{startIndex + idx + 1}</span>
                    <span className="billing__card-id"><Highlight text={invoice.id} query={searchQuery} /></span>
                  </div>
                  <div className="billing__card-header-row billing__card-header-row--bottom">
                    <span className={`billing__order-type-badge billing__order-type-badge--${normalizeOrderType(invoice.orderType)}`}>
                      {normalizeOrderType(invoice.orderType) === 'sub' ? 'Sub' : 'Own'}
                    </span>
                    <span className="billing__card-status">
                      <span className={`billing__status billing__status--${invoice.status} billing__status--view`}>
                        {getStatusIcon(invoice.status)}
                        {invoice.status && invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </span>
                  </div>
                </div>
                
                <div className="billing__card-client">
                  <div className="billing__card-avatar">
                    {invoice.clientName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="billing__card-client-info">
                    <h3><Highlight text={invoice.clientName} query={searchQuery} /></h3>
                    {String(invoice.clientPhone || '').trim() && (
                      <p><Highlight text={invoice.clientPhone} query={searchQuery} /></p>
                    )}
                    {String(invoice.clientEmail || '').trim() && (
                      <p><Highlight text={invoice.clientEmail} query={searchQuery} /></p>
                    )}
                  </div>
                </div>

                {Array.isArray(invoice.services) && invoice.services.length > 0 && (
                  <ServiceTags
                    services={invoice.services || []}
                    containerClass="billing__card-services"
                    tagClass="billing__service-tag"
                    moreClass="billing__service-more"
                    maxRows={2}
                    highlightQuery={searchQuery}
                  />
                )}

                    <div className="billing__card-amounts">
                  <div className="billing__card-amount-item">
                    <label>{t('billing.colTotalAmt')}</label>
                    <span className="billing__amount-value">₹{invoice.amount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="billing__card-amount-item">
                    <label>{t('common.paid')}</label>
                    <span className="billing__paid-value">₹{invoice.paid.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="billing__card-amount-item billing__card-amount-balance">
                    <label>{t('billing.colBalance')}</label>
                    <span className="billing__balance-value">₹{(invoice.amount - invoice.paid).toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {String(invoice.paymentMethod || '').trim() && (
                  <div className="billing__card-details">
                    <div className="billing__card-detail">
                      <FaMoneyBillWave />
                      <div>
                        <label>{t('billing.colPaymentMethod')}</label>
                        <span>{formatPaymentMethod(invoice.paymentMethod)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="billing__card-actions">
                  <button
                    className="billing__action-btn billing__action-btn--view"
                    onClick={() => handleViewInvoice(invoice)}
                    title="View"
                  >
                    <FaEye /> {t('billing.viewBtn')}
                  </button>
                  {invoice.status !== 'paid' && (
                    <button
                      className="billing__action-btn billing__action-btn--payment"
                      onClick={() => handleRecordPayment(invoice)}
                      title="Record Payment"
                    >
                      <FaMoneyBillWave />
                    </button>
                  )}
                  <button
                    className="billing__action-btn billing__action-btn--download"
                    onClick={() => handleDownloadInvoice(invoice)}
                    title="Download PDF"
                  >
                    <FaDownload />
                  </button>
                  <button
                    className="billing__action-btn billing__action-btn--whatsapp"
                    title="Send WhatsApp"
                    onClick={() => handleWhatsApp(invoice)}
                  >
                    <FaWhatsapp />
                  </button>
                  <button
                    className="billing__action-btn billing__action-btn--email"
                    title="Send Email"
                    onClick={() => handleSendEmail(invoice)}
                  >
                    <FaEnvelope />
                  </button>
                  <button
                    className="billing__action-btn billing__action-btn--delete"
                    onClick={() => handleDeleteInvoice(invoice.id)}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredInvoices.length > 0 && (
        <div className="billing__pagination">
          <div className="billing__pagination-info">
            {t('common.showing')} {startIndex + 1} {t('common.to')} {Math.min(endIndex, filteredInvoices.length)} {t('common.of')} {filteredInvoices.length} {t('billing.invoicesLabel')}
          </div>
          <div className="billing__pagination-controls">
            <div className="billing__pagination-rows">
              <label>{t('common.rowsPerPage')}</label>
              <CustomSelect
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                options={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                ]}
              />
            </div>
            <div className="billing__pagination-buttons">
              <button
                className="billing__pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First Page"
              >
                <FaAngleDoubleLeft />
              </button>
              <button
                className="billing__pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <FaAngleLeft />
              </button>
              <div className="billing__pagination-pages">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        className="billing__pagination-btn"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="billing__pagination-ellipsis">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`billing__pagination-btn ${currentPage === i ? 'active' : ''}`}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="billing__pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        className="billing__pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>
              <button
                className="billing__pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next Page"
              >
                <FaAngleRight />
              </button>
              <button
                className="billing__pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last Page"
              >
                <FaAngleDoubleRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal (render children only when selectedInvoice exists) */}
      {showPaymentModal && selectedInvoice && (
        <Modal
          isOpen={true}
          contentClassName="billing__modal"
          title={`Record Payment - ${selectedInvoice.id}`}
          onClose={() => setShowPaymentModal(false)}
        >
          <div className="billing__payment-form">
            <div className="billing__payment-summary">
              <div className="billing__payment-summary-row">
                <span>{t('billing.labelTotalAmt')}</span>
                <span>₹{selectedInvoice.amount.toLocaleString('en-IN')}</span>
              </div>
              <div className="billing__payment-summary-row">
                <span>{t('billing.labelAlreadyPaid')}</span>
                <span className="billing__payment-paid">₹{selectedInvoice.paid.toLocaleString('en-IN')}</span>
              </div>
              <div className="billing__payment-summary-row billing__payment-summary-balance">
                <span>{t('billing.labelOutstanding')}</span>
                <span>₹{(selectedInvoice.amount - selectedInvoice.paid).toLocaleString('en-IN')}</span>
              </div>
            </div>

              <div className="billing__form-row billing__form-row--2col">
                <div className="billing__form-group">
                  <label>{t('billing.labelPaymentAmt')}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>

                <div className="billing__form-group">
                  <label>{t('billing.labelPaymentDate')}</label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  />
                </div>
              </div>

            <div className="billing__form-group billing__form-group--payment-method">
              <label>{t('billing.labelPaymentMethod')}</label>
              <CustomSelect
                value={formatPaymentMethod(paymentData.paymentMethod)}
                onChange={(value) => setPaymentData({ ...paymentData, paymentMethod: value })}
                options={[
                  { value: 'Bank Transfer', label: t('billing.methodBankTransfer') },
                  { value: 'UPI', label: t('billing.methodUpi') },
                  { value: 'Cash', label: t('billing.methodCash') },
                  { value: 'Cheque', label: t('billing.methodCheque') },
                ]}
                className="billing__payment-method-select"
              />
            </div>

            {/* <div className="billing__form-group">
              <label>Reference Number</label>
              <input
                type="text"
                placeholder="Transaction ID, Check number, etc."
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
              />
            </div> */}

            <div className="billing__form-group">
              <label>{t('common.notes')}</label>
              <textarea
                placeholder="Additional payment notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="billing__form-actions">
              <button className="btn-secondary" onClick={() => setShowPaymentModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmitPayment}
                disabled={!paymentData.amount || paymentData.amount <= 0}
              >
                <FaCheck /> Record Payment
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <Modal
          isOpen={true}
          contentClassName="billing__modal"
          title={editingInvoiceId ? "Edit Invoice" : "Create Invoice"}
          onClose={() => { saveBillingDraftToSession(BILLING_DRAFT_KEY); setShowCreateModal(false); setEditingInvoiceId(null); }}
          size="large"
          headerActions={
            <div className="billing__header-actions-group">
              <div className="billing__order-type-toggle">
                <span className={`billing__order-label${newInvoiceData.orderType !== 'sub' ? ' billing__order-label--active' : ''}`}>Own</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={newInvoiceData.orderType === 'sub'}
                    onChange={() => setNewInvoiceData({ ...newInvoiceData, orderType: newInvoiceData.orderType === 'sub' ? 'own' : 'sub' })}
                  />
                  <span className="slider" />
                </label>
                <span className={`billing__order-label${newInvoiceData.orderType === 'sub' ? ' billing__order-label--active' : ''}`}>Sub</span>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={(e) => { e.stopPropagation && e.stopPropagation(); handleResetCreateForm(); }}
                title="Reset form"
              >
                Reset
              </button>
            </div>
          }
        >
          <div className="billing__form">
            <div className="billing__form-section">
              <div className="billing__form-row billing__form-row--3col">
                <div className="billing__form-group">
                  <label>{t('billing.labelInvoiceId')}</label>
                  <input type="text" readOnly value={previewInvoiceId || ''} />
                </div>
                <div className="billing__form-group">
                  <div className="billing__label-with-checkbox">
                    <div className="billing__mode-switch">
                      <span className={referenceMode === 'quotation' ? 'billing__mode-label billing__mode-label--active' : 'billing__mode-label'}>QTN ID</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={referenceMode === 'event'}
                          onChange={() => {
                            const newMode = referenceMode === 'quotation' ? 'event' : 'quotation';
                            setReferenceMode(newMode);
                            setNewInvoiceData({ ...newInvoiceData, quotationId: '', eventId: '' });
                            setQuotationQuery('');
                            setEventQuery('');
                            setShowQuotationDropdown(false);
                            setShowEventDropdown(false);
                          }}
                        />
                        <span className="slider" />
                      </label>
                      <span className={referenceMode === 'event' ? 'billing__mode-label billing__mode-label--active' : 'billing__mode-label'}>EVNT ID</span>
                    </div>
                    <label className="billing__toggle-label">
                      <span className="switch switch--small">
                        <input
                          type="checkbox"
                          checked={useQuotationData}
                          onChange={(e) => setUseQuotationData(e.target.checked)}
                        />
                        <span className="slider" />
                      </span>
                      <span>{t('common.showInPdf')}</span>
                    </label>
                  </div>
                  {(() => {
                    if (referenceMode === 'quotation') {
                      // Quotation mode
                      const allDropdown = getDropdownQuotations() || [];
                      const hasList = Array.isArray(allDropdown) && allDropdown.length > 0;

                      if (hasList) {
                        const filtered = allDropdown.filter(q => {
                          const label = String(q.id || q.quotationId || q.ref) + (q.clientName ? ' - ' + q.clientName : '');
                          return label.toLowerCase().includes((quotationQuery || '').toLowerCase());
                        });

                        // derive display value: if a quotationId already selected and query empty, show its label
                        const selectedLabel = (() => {
                          if (quotationQuery) return quotationQuery;
                          if (newInvoiceData.quotationId) {
                            const found = allDropdown.find(x => String(x.id || x.quotationId || x.ref) === String(newInvoiceData.quotationId));
                            if (found) return String(found.id || found.quotationId || found.ref) + (found.clientName ? ' - ' + found.clientName : '');
                          }
                          return '';
                        })();

                        return (
                          <div style={{ position: 'relative' }}>
                            <input
                              ref={quotationInputRef}
                              className="billing__quotation-input"
                              type="text"
                              placeholder="Search quotation by ID or client name"
                              value={quotationQuery || selectedLabel}
                              onChange={(e) => { setQuotationQuery(e.target.value); setShowQuotationDropdown(true); setHighlightedQuotationIndex(-1); }}
                              onFocus={() => { setShowQuotationDropdown(true); setHighlightedQuotationIndex(-1); }}
                              onKeyDown={(e) => {
                                const max = filtered.length - 1;
                                if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedQuotationIndex(i => Math.min(max, Math.max(0, i + 1))); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedQuotationIndex(i => Math.max(0, i - 1)); }
                                else if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (highlightedQuotationIndex >= 0 && filtered[highlightedQuotationIndex]) {
                                    const sel = filtered[highlightedQuotationIndex];
                                    const selId = String(sel.id || sel.quotationId || sel.ref);
                                    handleSelectQuotation(selId);
                                    setQuotationQuery(String(selId) + (sel.clientName ? ' - ' + sel.clientName : ''));
                                    setShowQuotationDropdown(false);
                                    setHighlightedQuotationIndex(-1);
                                  }
                                } else if (e.key === 'Escape') {
                                  setShowQuotationDropdown(false);
                                }
                              }}
                              onBlur={() => { quotationBlurTimeoutRef.current = setTimeout(() => setShowQuotationDropdown(false), 120); }}
                              onMouseDown={() => { if (quotationBlurTimeoutRef.current) { clearTimeout(quotationBlurTimeoutRef.current); quotationBlurTimeoutRef.current = null; } }}
                            />

                            {showQuotationDropdown && filtered.length > 0 && (
                              <ul className="billing__quotation-dropdown" style={{ position: 'absolute', zIndex: 40, top: 'calc(100% + 6px)', left: 0, right: 0, maxHeight: 220, overflow: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 0, margin: 0, listStyle: 'none' }}>
                                {filtered.map((q, idx) => {
                                  const label = String(q.id || q.quotationId || q.ref) + (q.clientName ? ' - ' + q.clientName : '');
                                  const isHighlighted = idx === highlightedQuotationIndex;
                                  return (
                                    <li
                                      key={(q.id || q.quotationId || q.ref) + '-' + idx}
                                      onMouseDown={(ev) => { ev.preventDefault(); const selId = String(q.id || q.quotationId || q.ref); handleSelectQuotation(selId); setQuotationQuery(label); setShowQuotationDropdown(false); setHighlightedQuotationIndex(-1); }}
                                      onMouseEnter={() => setHighlightedQuotationIndex(idx)}
                                      style={{ padding: '8px 10px', background: isHighlighted ? '#f3f4f6' : '#fff', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                    >
                                        <div style={{ fontSize: 13, color: '#111' }}>{label}</div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}

                              {/* Clear / Cancel button */}
                              {(quotationQuery || newInvoiceData.quotationId) && (
                                <button
                                  type="button"
                                  className="billing__quotation-clear"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setQuotationQuery('');
                                    setNewInvoiceData(getEmptyInvoiceData());
                                    setShowQuotationDropdown(false);
                                    setHighlightedQuotationIndex(-1);
                                    try { quotationInputRef.current && quotationInputRef.current.focus(); } catch (err) {}
                                  }}
                                  aria-label="Clear quotation"
                                >
                                  <FaTimes />
                                </button>
                              )}
                          </div>
                        );
                      }

                      return (
                          <div className="billing__quotation-empty-state">
                            <div className="billing__quotation-empty-message">No quotations available</div>
                          </div>
                        );
                    } else {
                      // Event mode
                      const allDropdown = getDropdownEvents() || [];
                      const hasList = Array.isArray(allDropdown) && allDropdown.length > 0;

                      if (hasList) {
                        const filtered = allDropdown.filter(ev => {
                          const label = String(ev.id || ev.quotationId) + (ev.clientName ? ' - ' + ev.clientName : '');
                          return label.toLowerCase().includes((eventQuery || '').toLowerCase());
                        });

                        // derive display value: if an eventId already selected and query empty, show its label
                        const selectedLabel = (() => {
                          if (eventQuery) return eventQuery;
                          if (newInvoiceData.eventId) {
                            const found = allDropdown.find(x => String(x.id || x.quotationId) === String(newInvoiceData.eventId));
                            if (found) return String(found.id || found.quotationId) + (found.clientName ? ' - ' + found.clientName : '');
                          }
                          return '';
                        })();

                        return (
                          <div style={{ position: 'relative' }}>
                            <input
                              ref={eventInputRef}
                              className="billing__quotation-input"
                              type="text"
                              placeholder="Search event by ID or client name"
                              value={eventQuery || selectedLabel}
                              onChange={(e) => { setEventQuery(e.target.value); setShowEventDropdown(true); setHighlightedEventIndex(-1); }}
                              onFocus={() => { setShowEventDropdown(true); setHighlightedEventIndex(-1); }}
                              onKeyDown={(e) => {
                                const max = filtered.length - 1;
                                if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedEventIndex(i => Math.min(max, Math.max(0, i + 1))); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedEventIndex(i => Math.max(0, i - 1)); }
                                else if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (highlightedEventIndex >= 0 && filtered[highlightedEventIndex]) {
                                    const sel = filtered[highlightedEventIndex];
                                    const selId = String(sel.id || sel.quotationId);
                                    handleSelectEvent(selId);
                                    setEventQuery(String(selId) + (sel.clientName ? ' - ' + sel.clientName : ''));
                                    setShowEventDropdown(false);
                                    setHighlightedEventIndex(-1);
                                  }
                                } else if (e.key === 'Escape') {
                                  setShowEventDropdown(false);
                                }
                              }}
                              onBlur={() => { eventBlurTimeoutRef.current = setTimeout(() => setShowEventDropdown(false), 120); }}
                              onMouseDown={() => { if (eventBlurTimeoutRef.current) { clearTimeout(eventBlurTimeoutRef.current); eventBlurTimeoutRef.current = null; } }}
                            />

                            {showEventDropdown && filtered.length > 0 && (
                              <ul className="billing__quotation-dropdown" style={{ position: 'absolute', zIndex: 40, top: 'calc(100% + 6px)', left: 0, right: 0, maxHeight: 220, overflow: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 0, margin: 0, listStyle: 'none' }}>
                                {filtered.map((ev, idx) => {
                                  const label = String(ev.id || ev.quotationId) + (ev.clientName ? ' - ' + ev.clientName : '');
                                  const isHighlighted = idx === highlightedEventIndex;
                                  return (
                                    <li
                                      key={(ev.id || ev.quotationId) + '-' + idx}
                                      onMouseDown={(e) => { e.preventDefault(); const selId = String(ev.id || ev.quotationId); handleSelectEvent(selId); setEventQuery(label); setShowEventDropdown(false); setHighlightedEventIndex(-1); }}
                                      onMouseEnter={() => setHighlightedEventIndex(idx)}
                                      style={{ padding: '8px 10px', background: isHighlighted ? '#f3f4f6' : '#fff', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                    >
                                        <div style={{ fontSize: 13, color: '#111' }}>{label}</div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}

                              {/* Clear / Cancel button */}
                              {(eventQuery || newInvoiceData.eventId) && (
                                <button
                                  type="button"
                                  className="billing__quotation-clear"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setEventQuery('');
                                    setNewInvoiceData(getEmptyInvoiceData());
                                    setShowEventDropdown(false);
                                    setHighlightedEventIndex(-1);
                                    try { eventInputRef.current && eventInputRef.current.focus(); } catch (err) {}
                                  }}
                                  aria-label="Clear event"
                                >
                                  <FaTimes />
                                </button>
                              )}
                          </div>
                        );
                      }

                      return (
                          <div className="billing__quotation-empty-state">
                            <div className="billing__quotation-empty-message">No events available</div>
                          </div>
                        );
                    }
                  })()}

                </div>
                <div className="billing__form-group">
                  <label>{t('billing.labelIssueDate')}</label>
                  <input
                    type="date"
                    value={newInvoiceData.issueDate}
                    onChange={(e) => setNewInvoiceData({ ...newInvoiceData, issueDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="billing__form-row billing__form-row--3col">
                <div className="billing__form-group">
                  <label>{t('billing.labelClientName')}</label>
                  <input
                    type="text"
                    placeholder="Enter client name"
                    value={newInvoiceData.clientName}
                    // allow letters and spaces only
                    onChange={(e) => {
                      const filtered = (e.target.value || '').replace(/[^A-Za-z\s]/g, '');
                      setNewInvoiceData({ ...newInvoiceData, clientName: filtered });
                    }}
                    pattern="[A-Za-z ]+"
                  />
                </div>
                <div className="billing__form-group">
                    <label>{t('billing.labelClientPhone')}</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    title="Phone: 10 digits, numbers only"
                    value={newInvoiceData.clientPhone}
                    // allow digits only, max 10 digits
                    onChange={(e) => {
                      const digits = (e.target.value || '').toString().replace(/\D/g, '').slice(0, 10);
                      setNewInvoiceData({ ...newInvoiceData, clientPhone: digits });
                    }}
                  />
                </div>
                <div className="billing__form-group">
                    <label>{t('billing.labelClientEmail')}</label>
                  <input
                    type="email"
                    placeholder="client@email.com"
                    value={newInvoiceData.clientEmail}
                    onChange={(e) => setNewInvoiceData({ ...newInvoiceData, clientEmail: (e.target.value || '').trim() })}
                  />
                </div>
              </div>
            </div>

            <div className="billing__form-section">
              <div className="billing__form-section-header">
                <h3>{t('billing.sectionServices')}</h3>
                <button className="btn-secondary billing__add-item-btn" onClick={addLineItem} type="button">
                  <FaPlus /> {t('common.addItem')}
                </button>
              </div>

              {newInvoiceData.items.map((item, index) => (
                <div key={index} className="billing__line-item">
                  {/* Remove button - positioned in top right corner */}
                  {newInvoiceData.items.length > 1 && (
                    <button
                      className="billing__remove-item"
                      onClick={() => removeLineItem(index)}
                      title="Remove item"
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  )}

                  {/* Service field - full width row */}
                  <div className="billing__line-item-row billing__line-item-row--service">
                    <div className="billing__form-group billing__form-group--wide">
                      <label>{t('quotation.colService')}</label>
                      <ServiceTypeInput
                        value={item.service || ''}
                        onChange={(val) => selectService(index, val)}
                        availableServices={allServices}
                        onNewService={handleNewService}
                      />
                    </div>
                  </div>

                  {/* Quantity, Price, Total row */}
                  <div className="billing__line-item-row billing__line-item-row--details">
                    <div className="billing__form-group">
                      <label>{t('quotation.colQty')}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="billing__form-group">
                      <label>{t('quotation.colPrice')}</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*\.?[0-9]*"
                        value={item.price}
                        onChange={(e) => updateLineItem(index, 'price', e.target.value)}
                      />
                    </div>
                    <div className="billing__form-group">
                      <label className="billing__label--right">{t('quotation.colTotal')}</label>
                      <div className="billing__line-total">
                        ₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>

                  {/* Description - always shown below */}
                  <div className="billing__form-group">
                    <label>{t('quotation.colDescOpt')}</label>
                    <input
                      type="text"
                      placeholder="Additional details about this service"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
              ))}

              <div className="billing__summary">
                <div className="billing__summary-row">
                  <span>{t('common.subtotal')}</span>
                  <span>₹{calculateSubtotal().toLocaleString('en-IN')}</span>
                </div>

                {/* Additional Charges */}
                {(newInvoiceData.additionalCharges || []).length > 0 && (
                  <div className="billing__additional-charges">
                    {(newInvoiceData.additionalCharges || []).map((c, i) => (
                      <div key={i} className="billing__charge-row">
                        <div className="billing__form-group billing__charge-desc billing__charge-desc--flex1">
                          <label>
                            {t('common.charge')}
                            <button
                              type="button"
                              onClick={() => removeAdditionalCharge(i)}
                              className="billing__remove-item billing__charge-remove"
                              aria-label="Remove charge"
                            >
                              <FaTimes />
                            </button>
                          </label>
                          <input
                            type="text"
                            placeholder="Charge description"
                            value={c.description}
                            onChange={(e) => updateAdditionalCharge(i, 'description', e.target.value)}
                          />
                        </div>

                        <div className="billing__form-group billing__charge-amount billing__charge-amount--fixed">
                          <label>{t('common.amount')}</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            value={c.amount}
                            onChange={(e) => updateAdditionalCharge(i, 'amount', e.target.value)}
                          />
                        </div>
                        
                      </div>
                    ))}
                  </div>
                )}

                <div className="billing__add-charge-row">
                  <button type="button" onClick={addAdditionalCharge} className="btn-secondary">
                    {t('common.addCharge')}
                  </button>
                </div>

                <div className="billing__summary-row billing__summary-total">
                  <span>{t('common.total')}</span>
                  <span>₹{calculateTotal().toLocaleString('en-IN')}</span>
                </div>

                {/* Terms & Conditions */}
                <div className="billing__form-group billing__terms-section" style={{ marginTop: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                      Terms &amp; Conditions
                    </label>
                    <label className="billing__toggle-option billing__toggle-option--inline">
                      <span className="switch switch--small">
                        <input
                          type="checkbox"
                          checked={newInvoiceData.showTermsInPdf !== false}
                          onChange={(e) => setNewInvoiceData({ ...newInvoiceData, showTermsInPdf: e.target.checked })}
                        />
                        <span className="slider" />
                      </span>
                      <span>{t('common.showInPdf')}</span>
                    </label>
                  </div>

                  {Object.keys(groupedPolicies).length === 0 ? (
                    <div style={{ 
                      padding: '1.5rem', 
                      background: 'var(--color-surface-muted)', 
                      border: '2px dashed var(--color-border)', 
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: 'var(--color-text-muted)'
                    }}>
                      <p style={{ margin: '0 0 0.5rem 0' }}>No terms and conditions available.</p>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        Go to the Policy page to add your terms and conditions.
                      </p>
                    </div>
                  ) : (
                    <div className="billing__policy-groups">
                      {Object.entries(groupedPolicies).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, policies]) => (
                        <div key={groupName} className="billing__policy-group" style={{
                          marginBottom: '1rem',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          {/* Group Header */}
                          <div style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            padding: '0.6rem 0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                          }}>
                            <label style={{
                              margin: 0,
                              color: '#ffffff',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.45rem',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}>
                              <input
                                type="checkbox"
                                className="billing__group-checkbox"
                                checked={isGroupFullySelected(groupName)}
                                ref={el => {
                                  if (el) el.indeterminate = isGroupPartiallySelected(groupName);
                                }}
                                onChange={(e) => handleSelectGroup(groupName, e.target.checked)}
                                style={{ 
                                  cursor: 'pointer',
                                  accentColor: '#ffffff'
                                }}
                              />
                              <span>📋 {groupName}</span>
                            </label>
                            <span style={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '0.2rem 0.6rem',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '500',
                              color: '#ffffff'
                            }}>
                              {policies.length} term{policies.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Group Terms */}
                          <div style={{ padding: '0.6rem 0.75rem', background: 'var(--color-surface)' }}>
                            {policies.map((policy, idx) => (
                              <label
                                key={policy.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '0.45rem 0.55rem',
                                  marginBottom: idx === policies.length - 1 ? 0 : '0.35rem',
                                  cursor: 'pointer',
                                  borderRadius: '6px',
                                  transition: 'all 0.18s ease',
                                  background: (newInvoiceData.terms || []).includes(policy.content) ? 'var(--color-success-soft)' : 'transparent',
                                  border: (newInvoiceData.terms || []).includes(policy.content) ? '1px solid var(--color-success-border)' : '1px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (!(newInvoiceData.terms || []).includes(policy.content)) {
                                    e.currentTarget.style.background = 'var(--color-surface-hover)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!(newInvoiceData.terms || []).includes(policy.content)) {
                                    e.currentTarget.style.background = 'transparent';
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="billing__term-checkbox"
                                  checked={(newInvoiceData.terms || []).includes(policy.content)}
                                  onChange={() => handleTermToggle(policy.content)}
                                  style={{ 
                                    marginTop: '0',
                                    marginRight: '0.6rem',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    accentColor: '#10b981',
                                    alignSelf: 'center'
                                  }}
                                />
                                <span style={{ fontSize: '14.5px', color: 'var(--color-text)', lineHeight: '1.4', fontWeight: '500', display: 'flex', alignItems: 'center' }}>{policy.content}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(newInvoiceData.terms || []).filter(term => Object.values(groupedPolicies).flat().some(policy => policy.content === term)).length > 0 && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'var(--color-success-soft)',
                      border: '1px solid var(--color-success-border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--color-success-dark)'
                    }}>
                      <strong>{newInvoiceData.terms.filter(term => Object.values(groupedPolicies).flat().some(policy => policy.content === term)).length}</strong> term{newInvoiceData.terms.filter(term => Object.values(groupedPolicies).flat().some(policy => policy.content === term)).length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              </div>

              {/* PDF Options */}
                <div className="billing__toggle-options" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                  <label className="billing__toggle-option billing__toggle-option--bottom">
                    <span className="switch switch--small">
                      <input
                        type="checkbox"
                        checked={newInvoiceData.showDescriptionInPdf === true}
                        onChange={(e) => setNewInvoiceData({ ...newInvoiceData, showDescriptionInPdf: e.target.checked })}
                      />
                      <span className="slider" />
                    </span>
                    <span>Show Description in PDF</span>
                  </label>
                  <label className="billing__toggle-option billing__toggle-option--bottom">
                    <span className="switch switch--small">
                      <input
                        type="checkbox"
                        checked={newInvoiceData.showPaymentDetailsInPdf !== false}
                        onChange={(e) => setNewInvoiceData({ ...newInvoiceData, showPaymentDetailsInPdf: e.target.checked })}
                      />
                      <span className="slider" />
                    </span>
                    <span>Show Payment Details in PDF</span>
                  </label>
                  <label className="billing__toggle-option billing__toggle-option--bottom">
                    <span className="switch switch--small">
                      <input
                        type="checkbox"
                        checked={newInvoiceData.showQrCodeInPdf !== false}
                        onChange={(e) => setNewInvoiceData({ ...newInvoiceData, showQrCodeInPdf: e.target.checked })}
                      />
                      <span className="slider" />
                    </span>
                    <span>Show QR Code in PDF</span>
                  </label>
                </div>
            </div>

            <div className="billing__form-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  try { saveBillingDraftToSession(makeBillingDraftKey()); } catch (e) {}
                  try { setShowCreateModal(false); } catch (e) {}
                  try { showToast('⚠️ your form saved temporarily, please complete and submit soon.'); } catch (e) {}
                }}
                type="button"
              >
                Save Draft
              </button>
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)} type="button">
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCreateInvoice}
                disabled={!newInvoiceData.clientName || !/^\d{10}$/.test(newInvoiceData.clientPhone || '') || newInvoiceData.items.every(item => !item.service)}
                type="button"
              >
                <FaCheck /> Create Invoice
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* View Invoice Modal (render children only when selectedInvoice exists) */}
      {showViewModal && selectedInvoice && (
        <Modal
          isOpen={true}
          contentClassName="billing__modal"
          title={`Invoice ${selectedInvoice.id}`}
          headerActions={<button className="btn-secondary" onClick={() => handleEditFromView(selectedInvoice)} title="Edit invoice"><FaEdit /> Edit</button>}
          onClose={() => setShowViewModal(false)}
        >
          <div className="billing__invoice-view">
            <div className="billing__invoice-header">
              <div className="billing__invoice-client">
                <h3>{selectedInvoice.clientName}</h3>
                <p>{selectedInvoice.clientPhone}</p>
                <p>{selectedInvoice.clientEmail}</p>
              </div>
              <span className={`billing__status billing__status--${selectedInvoice.status} billing__status--view`}>
                {getStatusIcon(selectedInvoice.status)}
                {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
              </span>
            </div>

            <div className="billing__invoice-section">
              <div className="billing__invoice-row">
                <div>
                  <label>Issue Date:</label>
                  <span>{formatDateDisplay(selectedInvoice.issueDate)}</span>
                </div>
                {selectedInvoice.showQuotationIdInPdf !== false && (selectedInvoice.eventId || selectedInvoice.quotationId) && (
                  <div className="billing__invoice-row-right">
                    <label>{selectedInvoice.eventId ? 'Event ID:' : 'Quotation ID:'}</label>
                    <span>{selectedInvoice.eventId || selectedInvoice.quotationId || ''}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="billing__invoice-section">
              <h4>{t('billing.sectionServices')}</h4>
              {(() => {
                const invoiceItems = Array.isArray(selectedInvoice.items) && selectedInvoice.items.length > 0
                  ? selectedInvoice.items
                  : (Array.isArray(selectedInvoice.services)
                      ? selectedInvoice.services.map((serviceName) => {
                          const matchedService = availableServices.find((s) => s.name === serviceName);
                          return {
                            service: serviceName,
                            quantity: 1,
                            price: matchedService?.price || 0
                          };
                        })
                      : []);

                if (!invoiceItems.length) {
                  return <p className="billing__invoice-empty">{t('billing.noServiceItems')}</p>;
                }

                return (
                  <div className="billing__invoice-items-wrap">
                    <table className="billing__invoice-items-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th className="billing__invoice-items-center">Qty</th>
                          <th className="billing__invoice-items-right">Unit Price</th>
                          <th className="billing__invoice-items-right">Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceItems.map((item, idx) => {
                          const quantity = parseFloat(item.quantity) || 0;
                          const unitPrice = parseFloat(item.price) || 0;
                          const lineTotal = quantity * unitPrice;

                          return (
                            <tr key={`${item.service || 'item'}-${idx}`}>
                              <td>{item.service || '-'}</td>
                              <td className="billing__invoice-items-center">{quantity}</td>
                              <td className="billing__invoice-items-right">₹{unitPrice.toLocaleString('en-IN')}</td>
                              <td className="billing__invoice-items-right">₹{lineTotal.toLocaleString('en-IN')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            <div className="billing__invoice-section">
              <h4>{t('common.sectionCharges')}</h4>
              {Array.isArray(selectedInvoice.additionalCharges) && selectedInvoice.additionalCharges.length > 0 ? (
                <div className="billing__invoice-additional-charges">
                  {selectedInvoice.additionalCharges.map((charge, idx) => (
                    <div key={`charge-${idx}`} className="billing__invoice-charge-row">
                      <span className="billing__invoice-charge-desc">{charge?.description || 'Additional charge'}</span>
                      <span className="billing__invoice-charge-amount">₹{(parseFloat(charge?.amount) || 0).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="billing__invoice-empty">{t('billing.noCharges')}</p>
              )}
            </div>

            <div className="billing__invoice-section">
              <div className="billing__invoice-totals">
                <div className="billing__invoice-total-row">
                  <span>{t('billing.labelTotalAmt2')}</span>
                  <span>₹{selectedInvoice.amount.toLocaleString('en-IN')}</span>
                </div>
                <div className="billing__invoice-total-row">
                  <span>{t('billing.labelAmountPaid')}</span>
                  <span className="billing__payment-paid">₹{selectedInvoice.paid.toLocaleString('en-IN')}</span>
                </div>
                <div className="billing__invoice-total-row billing__invoice-total-balance">
                  <span>{t('billing.labelBalanceDue')}</span>
                  <span>₹{(selectedInvoice.amount - selectedInvoice.paid).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            <div className="billing__invoice-section">
              <h4>{t('billing.repaymentHistory')}</h4>
              {selectedInvoice.payments && selectedInvoice.payments.length > 0 ? (
                <ul className="billing__repayment-history">
                  {selectedInvoice.payments.slice().reverse().map((p, idx) => (
                    <li key={idx} className="billing__repayment-item">
                      <div className="billing__repayment-row">
                        <span className="billing__repayment-amount">₹{(parseFloat(p.amount) || 0).toLocaleString('en-IN')}</span>
                        <span className="billing__repayment-date">{formatDateDisplay(p.date || p.recordedAt || p.paymentDate)}</span>
                      </div>
                      <div className="billing__repayment-meta">
                        <span className="billing__repayment-method">{formatPaymentMethod(p.method || p.paymentMethod)}</span>
                        {p.reference && <span className="billing__repayment-ref">{t('billing.labelRef')} {p.reference}</span>}
                        {p.notes && <span className="billing__repayment-notes">{p.notes}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('billing.noPayments')}</p>
              )}
            </div>

            <div className="billing__invoice-actions">
              {selectedInvoice.status !== 'paid' && (
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowViewModal(false);
                    handleRecordPayment(selectedInvoice);
                  }}
                >
                  <FaMoneyBillWave /> Record Payment
                </button>
              )}
              <button className="btn-secondary" onClick={() => handleDownloadInvoice(selectedInvoice)}>
                <FaDownload /> Download PDF
              </button>
              <button className="btn-secondary" onClick={() => handleSendEmail(selectedInvoice)}>
                <FaEnvelope /> Send Email
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* Share dialog: To Client vs To Others (WhatsApp / Email) */}
      <ShareChoiceModal
        isOpen={!!shareTarget}
        onClose={() => setShareTarget(null)}
        channel={shareChannel}
        title={shareChannel === 'whatsapp' ? 'Share Bill via WhatsApp' : 'Share Bill via Email'}
        docLabel={shareTarget ? `${shareTarget.id || ''}${shareTarget.clientName ? ` • ${shareTarget.clientName}` : ''}` : ''}
        clientName={shareTarget?.clientName || ''}
        clientContact={shareChannel === 'whatsapp' ? (shareTarget?.clientPhone || shareTarget?.client_phone || '') : (shareTarget?.clientEmail || '')}
        hasClientContact={shareChannel === 'whatsapp' ? !!normalizeSharePhone(shareTarget?.clientPhone || shareTarget?.client_phone) : !!((shareTarget?.clientEmail || '').trim())}
        clientMessage={shareTarget ? (shareChannel === 'whatsapp' ? buildInvoiceWhatsApp({ invoice: shareTarget, company, audience: 'client' }) : buildInvoiceEmail({ invoice: shareTarget, company, audience: 'client' }).body) : ''}
        othersMessage={shareTarget ? (shareChannel === 'whatsapp' ? buildInvoiceWhatsApp({ invoice: shareTarget, company, audience: 'others' }) : buildInvoiceEmail({ invoice: shareTarget, company, audience: 'others' }).body) : ''}
        clientSubject={shareTarget && shareChannel === 'email' ? buildInvoiceEmail({ invoice: shareTarget, company, audience: 'client' }).subject : ''}
        othersSubject={shareTarget && shareChannel === 'email' ? buildInvoiceEmail({ invoice: shareTarget, company, audience: 'others' }).subject : ''}
        onSendToClient={handleShareToClient}
        onShareOthersViaApp={handleShareOthersViaApp}
      />
    </div>
  );
};

export default Billing;
